import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Alert,
  Spin,
  Select,
  Button,
  Checkbox,
  Divider,
  Tooltip,
  InputNumber,
  Radio,
  Switch,
  message as antdMessage,
} from "antd";
import { faGears, faLock } from "@fortawesome/free-solid-svg-icons";
import * as flatbuffers from "flatbuffers";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
// INTERFACES
import { VendorFloorIndexing } from "../../../flatbuffers/generated/archive/vendor-floor-indexing";
import { FormularyType } from "../../../interfaces/Wizard.interface";
import { KEY_FLOOR_INDEXING } from "../../../interfaces/Key.interface";
// FLATBUFFERS
import {
  toVendorFloorIndexing,
  MAX_CARS_WIRE_NO_CAP,
} from "../../../flatbuffers/api/requestSerializers";
// COMPONENTS
import FormInputText from "../form/FormInputText";
import DrawerRowDivider from "../drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
import WizardReadOnlyId from "../../setupWizard/WizardReadOnlyId";
// CDO EDITOR
import StepAdaptersCdoEditor, {
  CdoStateBundle,
  isCdoSaveValid,
  isExclusionEditable,
  getCdoMissingMessage,
  buildCdoPayload,
} from "../../setupWizard/steps/StepAdaptersCdoEditor";
import type { EditorModbusTemplate } from "../../setupWizard/steps/CdoModbusEditor";
// CDO EXTRACTION
import { extractCdoState } from "../../edgeManagement/adapters/extractCdoState";
import {
  deserializeMappings,
  serializeMappings,
} from "../../edgeManagement/adapters/CDO/BACNET/CdoBacnetPage";
// REDUX
import {
  usePostAdapterMutation,
  usePutAdapterMutation,
  useLazyGetAdapterQuery,
} from "../../../redux/adapters/adapterAPI";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
// CUSTOM HOOKS
import useGetAllProtocolsLight from "../../../customHooks/GET_requests/useGetAllProtocolsLight";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import {
  BLANK_ADAPTER_CDO_MCE,
  BLANK_ADAPTER_CDO_LN1,
  BLANK_ADAPTER_CDO_OPC,
  BLANK_ADAPTER_CDO_OPCUA,
  BLANK_ADAPTER_CDO_LOBBYVISION2,
  BLANK_ADAPTER_CDO_SEQUENT,
} from "../../../constants/adapter";
// STORE
import { RootState } from "../../../app/store";
// FLATBUFFER ENUMS
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { AdapterAuth } from "../../../flatbuffers/generated/archive/adapter-auth";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { KeyDirection } from "../../../flatbuffers/generated/archive/key-direction";
import formatUuidT from "../../../flatbuffers/display/uuid";
import fetchAdapterDirect from "../../../flatbuffers/api/fetchAdapterDirect";
import {
  parseCompositeKey,
  LN2_MESSAGE_CATEGORIES,
  categoryKeyToMessageContent,
  messageContentToCategoryKey,
  buildMessageTForEnum,
  extractEnumValueFromMessageT,
  getDisplayName,
} from "../../../flatbuffers/types/ln2MessageOptions";
import {
  isGroupScopedCategory,
  type CategoryRegTypeMap,
  type MatrixColumnSpec,
} from "./offsetOverlapDetection";
import { PROTOCOL_REGISTRY } from "../../setupWizard/editors/ProtocolRegistry";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { MessageT } from "../../../flatbuffers/generated/ln2/message";
import { PasswordContent } from "../../../flatbuffers/generated/cdo/password-content";
import { OneHotExclusionConfigT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-config";
import { OneHotExclusionGroupT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-group";
import { OneHotExclusionEntryT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-entry";
// KEY MAPPING TYPES
import { Ln1KeyMappingRow } from "../../setupWizard/steps/CdoLn1MappingEditor";
// MODBUS FLATBUFFER BINARY
import {
  packModbusTemplate,
  unpackModbusTemplate,
  parseModbusRegisterOffsetsFromCdoTemplateJson,
  modbusRegisterOffsetsUiToFb,
} from "../../../utils/modbusFlatBuffer";
// BINARY CDO PROTOCOL REGISTRY (OPC UA / LOBBYVISION2)
import {
  OPC_UA_BINARY_CDO_CONFIG,
  LOBBYVISION2_BINARY_CDO_CONFIG,
  MCE_BINARY_CDO_CONFIG,
  getBinaryCdoProtocolByKey,
  type BinaryCdoProtocolConfig,
} from "../../setupWizard/editors/BinaryCdoProtocolRegistry";
import {
  packToBytes,
  sendFlatBufferBinary,
  fetchFlatBufferBinary,
} from "../../../utils/flatbufferApi";
import {
  decodeOneHotExclusion,
  oneHotExclusionToBase64,
} from "../../../flatbuffers/builders/oneHotExclusionBinary";
// BULK KEY FLATBUFFER TYPES
import { BulkKeyRequestT } from "../../../flatbuffers/generated/archive/bulk-key-request";
import { BulkKeyResponse } from "../../../flatbuffers/generated/archive/bulk-key-response";
import { KeyT } from "../../../flatbuffers/generated/archive/key";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import { deriveUnitCount } from "../../edgeManagement/keys/utils";
import {
  deriveInitialAndDelta,
  isLn1FamilyProtocol,
  migrateLegacyOffsetMap,
} from "./offsetMatrixHelpers";
import AdapterOffsetMatrix, {
  AdapterOffsetMap,
  AdapterOffsetColumn,
  computeAdapterOverlaps,
  MatrixKeyMappingSubset,
  buildMatrixColumnSpecs,
} from "./AdapterOffsetMatrix";
import OverlapAnalysisModal from "./OverlapAnalysisModal";
import { buildOverlapReport, contestedByteMessage } from "./overlapReportModel";
import { renderOverlapReportHtml } from "./overlapReportHtml";
import { openOverlapAnalysisWindow } from "./openOverlapAnalysisWindow";
import {
  updateOverlapAnalysisOpenIn,
  type OverlapAnalysisTarget,
} from "../../../redux/adapters/adapterSlice";
import {
  MAX_CARS_PROJECTION_CEILING,
  boundedAnalysisCarCount,
  MIN_AUTO_FILL_CARS,
  describeCarCollision,
  combineMaxCarsProjections,
  describeMaxCarsBound,
  isSharedAcrossCars,
  projectMaxCars,
  projectMaxCarsFromOffsetMapping,
  shouldAutoFillMaxCars,
  type MappingSeriesIdentity,
  type MappingSeriesResolver,
  type MaxCarsOrigin,
  type MaxCarsProjection,
  type OffsetMappingRow,
} from "./maxCarsProjection";
import { ModbusReadType } from "../../../flatbuffers/generated/config/modbus-read-type";
import { ModbusWriteType } from "../../../flatbuffers/generated/config/modbus-write-type";
import { extractLn1MappedTypes, MappedMessageType } from "../../setupWizard/steps/BankOffsetMatrix";
import { detectStaleServerErrorMessage, useWizardOptional } from "../../setupWizard/WizardContext";
import {
  matchLegacyEntryToColumn,
  remapLegacyMessageType,
  logUnresolvedBatch,
  dedupeUnresolvedEntries,
  type LegacyOffsetEntry,
} from "./legacySubTypeNormalizer";
import type { AdapterColumnDescriptor } from "./adapterColumnTypes";
import { devLog, logError } from "../../../utils/devLog";
import { downloadMceIdlLog } from "../../../utils/mceIdl/mceIdlLog";
import { base64ToBytes } from "../../../utils/base64";
import { refuseEventMapping } from "../../../utils/mceIdl/eventMappingCheck";
import {
  eventEditBody,
  eventEditFailure,
  eventEditReadBack,
  legacyEventEdit,
  mceAfterEventSave,
  type MceEventEdit,
} from "../../../utils/mceIdl/eventMappingEdit";
import {
  checkMappingOnServer,
  mceIfMatch,
  MceSaveFailure,
  READ_BACK_FAILED,
  readStoredMapping,
  saveUploadedMapping,
} from "../../../utils/mceIdl/saveUpload";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";

// ************************************************ */
// CONSTANTS ************************************** */
const PROTOCOL_OPTIONS = PROTOCOL_REGISTRY.filter((p) => p.value !== "BACNET").map((p) => p.value);

const LIFT_TYPE_OPTIONS = Object.keys(DevType)
  .filter((k) => Number.isNaN(Number(k)))
  .map((k) => k.toUpperCase());

const AUTH_TYPE_OPTIONS = ["NONE", "PASSWORD", "APIKEY", "JWT", "OAUTH"];

const LEADING_ZEROES_PROTOCOLS = PROTOCOL_REGISTRY.filter((p) => p.hasLeadingZeroes).map(
  (p) => p.value,
);

/**
 * Server wire format for a single offset_mapping entry. Mirrors the Go
 * `models.OffsetMappingEntry` struct — the backend stores offset_mapping
 * as a JSON-encoded []OffsetMappingEntry and rejects (then regenerates
 * to zero) anything it can't unmarshal as this shape.
 */
export interface OffsetMappingEntry {
  message_type: number;
  message_value?: number;
  message_sub_type?: string;
  unit_number: number;
  offset: number;
}

/**
 * Serialize cdoState's keyed offsets into the []OffsetMappingEntry array
 * the Go backend expects. Each (category | composite) emits `unitsToEmit`
 * entries — one per unit_number 0..unitsToEmit-1 — with the offset
 * computed as `initial + unit*delta`.
 *
 * Why expand all units (instead of just unit 0 + unit 1):
 *   1. Downstream key generation in the Go backend reads per-unit offsets
 *      from this array — emitting only 2 leaves units 2..N with no entry.
 *   2. The server's `packSimpleBinaryFields` size guard rejects writes
 *      whose JSON is < 50% of the existing binary length. Expanding to
 *      ~8 units keeps payloads dense enough to overwrite legacy data.
 *
 * Convention: pair[0] is the initial offset, pair[1] is initial + delta;
 * any units beyond that follow the same delta.
 */
const DEFAULT_OFFSET_UNITS = 8;

function unitOffset(pair: unknown[], unit: number): number | null {
  const initial = typeof pair[0] === "number" ? pair[0] : null;
  if (initial === null) return null;
  if (unit === 0) return initial;
  const second = typeof pair[1] === "number" ? pair[1] : initial;
  const delta = second - initial;
  return initial + unit * delta;
}

export function serializeOffsetMapping(
  ln1UnitOffsets: Record<string, unknown>,
  ln1IndividualOffsets: Record<string, unknown>,
  unitsToEmit: number = DEFAULT_OFFSET_UNITS,
): OffsetMappingEntry[] {
  const entries: OffsetMappingEntry[] = [];
  const units = Math.max(2, unitsToEmit);
  Object.entries(ln1UnitOffsets).forEach(([mcStr, raw]) => {
    const mc = Number(mcStr);
    if (!Number.isFinite(mc) || mc <= 0 || mc > 9) return;
    if (!Array.isArray(raw)) return;
    Array.from({ length: units }, (_, unit) => unit).forEach((unit) => {
      const offset = unitOffset(raw as unknown[], unit);
      if (offset === null) return;
      entries.push({ message_type: mc, unit_number: unit, offset });
    });
  });
  Object.entries(ln1IndividualOffsets).forEach(([compositeKey, raw]) => {
    const parsed = parseCompositeKey(compositeKey);
    if (!parsed || !Array.isArray(raw)) return;
    const mc = categoryKeyToMessageContent(parsed.categoryKey);
    if (mc === MessageContent.NONE) return;
    Array.from({ length: units }, (_, unit) => unit).forEach((unit) => {
      const offset = unitOffset(raw as unknown[], unit);
      if (offset === null) return;
      // Emit message_sub_type (variant) alongside message_value so load can
      // distinguish bank_level individual entries from plain grouped ones.
      // Go's `omitempty` on MsgValue drops value 0 during serialization, so
      // the variant string is the unambiguous discriminator.
      entries.push({
        message_type: mc,
        message_value: parsed.enumValue,
        message_sub_type: parsed.variant,
        unit_number: unit,
        offset,
      });
    });
  });
  return entries;
}

/**
 * Whether a stored offset_mapping array is in the CANONICAL encoding, where
 * `message_type` is a FlatBuffer `MessageContent` id, rather than the LEGACY
 * pre-schema one, where it is an old message_types-table id.
 *
 * Legacy is any entry with message_type 0 or with a non-empty sub_type other
 * than "normal"/"valued" (a friendly name from a pre-schema CSV import); a
 * mixed payload counts as legacy. Everything else, including a payload of
 * grouped-category entries that carry no sub_type at all, is canonical — that
 * is what `serializeOffsetMapping` writes for grouped categories.
 *
 * This is the single rule for reading `message_type`. `deserializeOffsetMapping`
 * (the adapter editor, and the per-column initial+delta defaults the bank
 * matrix falls back to) and the bank step's per-car seeding both use it, so the
 * two can never map the same entry to different columns.
 */
export function isCanonicalOffsetMapping(raw: readonly unknown[]): boolean {
  return !raw.some((entryRaw) => {
    if (!entryRaw || typeof entryRaw !== "object") return false;
    const entry = entryRaw as Record<string, unknown>;
    const sub = entry.message_sub_type;
    if (entry.message_type === 0) return true;
    return typeof sub === "string" && sub !== "" && sub !== "normal" && sub !== "valued";
  });
}

/**
 * Parse the server's []OffsetMappingEntry back into cdoState's keyed shape.
 *
 * One-pass format discriminator (PASS 0) classifies the entire payload as
 * CANONICAL or LEGACY before routing any entry:
 *  - CANONICAL: every sub_type is "normal", "valued", or empty; no mt=0
 *  - LEGACY: any entry has mt=0 OR any non-empty sub_type not in
 *    {"normal","valued"} (friendly name strings from pre-schema CSV imports)
 *  - MIXED: classified as LEGACY so partial-legacy payloads route correctly
 *
 * When `columns` is provided, legacy friendly-name entries are matched to
 * individual columns via normalised label comparison.  When absent, legacy
 * entries with non-empty sub_types become unresolved (back-compat behaviour).
 */
export function deserializeOffsetMapping(
  raw: unknown,
  columns?: AdapterColumnDescriptor[],
): {
  ln1UnitOffsets: Partial<Record<MessageContent, number[]>>;
  ln1IndividualOffsets: Partial<Record<string, [number, number]>>;
  unresolvedLegacyEntries: LegacyOffsetEntry[];
} {
  const grouped: Partial<Record<MessageContent, number[]>> = {};
  const individualAcc: Record<string, number[]> = {};
  const unresolved: LegacyOffsetEntry[] = [];

  if (!Array.isArray(raw)) {
    return {
      ln1UnitOffsets: grouped,
      ln1IndividualOffsets: {},
      unresolvedLegacyEntries: [],
    };
  }

  // PASS 0 — one-pass format discriminator.
  const format: "canonical" | "legacy" = isCanonicalOffsetMapping(raw) ? "canonical" : "legacy";

  raw.forEach((entryRaw) => {
    if (!entryRaw || typeof entryRaw !== "object") return;
    const entry = entryRaw as Record<string, unknown>;
    const mt = entry.message_type;
    if (typeof mt !== "number") return;
    const unit = typeof entry.unit_number === "number" ? entry.unit_number : 0;
    const offset = typeof entry.offset === "number" ? entry.offset : 0;
    if (unit < 0 || unit > 64) return;

    const subType = entry.message_sub_type;
    const subTypeStr = typeof subType === "string" ? subType : "";
    const mv = entry.message_value;

    if (format === "canonical") {
      if (mt <= 0 || mt > 9) return;
      const isCanonicalVariant = subTypeStr === "normal" || subTypeStr === "valued";
      if (isCanonicalVariant && typeof mv === "number") {
        const categoryKey = messageContentToCategoryKey(mt as MessageContent);
        if (!categoryKey) return;
        const compositeKey = `${categoryKey}:${subTypeStr}:${mv}`;
        const arr = individualAcc[compositeKey] ?? [];
        arr[unit] = offset;
        individualAcc[compositeKey] = arr;
        return;
      }
      const arr = grouped[mt as MessageContent] ?? [];
      arr[unit] = offset;
      grouped[mt as MessageContent] = arr;
      return;
    }

    // LEGACY routing
    const legacyEntry: LegacyOffsetEntry = {
      offset,
      unit_number: unit,
      message_type: mt,
      message_sub_type: subTypeStr !== "" ? subTypeStr : undefined,
      message_value: typeof mv === "number" ? mv : undefined,
    };
    const match = columns
      ? matchLegacyEntryToColumn(legacyEntry, columns)
      : { columnKey: null, confidence: "none" as const };

    if (
      (match.confidence === "exact" || match.confidence === "normalized") &&
      match.columnKey !== null &&
      match.columnKey.includes(":")
    ) {
      const arr = individualAcc[match.columnKey] ?? [];
      arr[unit] = offset;
      individualAcc[match.columnKey] = arr;
      return;
    }
    const canonicalMc = remapLegacyMessageType(mt);
    if (
      (match.confidence === "exact" || match.confidence === "normalized") &&
      canonicalMc > 0 &&
      canonicalMc <= 9
    ) {
      const arr = grouped[canonicalMc as MessageContent] ?? [];
      arr[unit] = offset;
      grouped[canonicalMc as MessageContent] = arr;
      return;
    }
    unresolved.push(legacyEntry);
  });

  // ISC-A9 — ONE aggregated warn per deserialize call, not per entry.
  logUnresolvedBatch(unresolved);

  const fillGaps = (arr: number[]): number[] => {
    if (arr.length === 0) return arr;
    const first = arr[0] ?? 0;
    if (arr.length === 1) return [first, first];
    const out = [...arr];
    out.forEach((_, i) => {
      if (out[i] === undefined) out[i] = i > 0 ? out[i - 1] : first;
    });
    return out;
  };
  Object.keys(grouped).forEach((k) => {
    const mc = Number(k) as MessageContent;
    const arr = grouped[mc];
    if (!arr) return;
    grouped[mc] = fillGaps(arr);
  });
  const individual: Partial<Record<string, [number, number]>> = {};
  Object.keys(individualAcc).forEach((k) => {
    const filled = fillGaps(individualAcc[k]);
    individual[k] = [filled[0] ?? 0, filled[1] ?? filled[0] ?? 0];
  });

  return {
    ln1UnitOffsets: grouped,
    ln1IndividualOffsets: individual,
    unresolvedLegacyEntries: unresolved,
  };
}

/**
 * Build the Offset Matrix columns from an adapter's own key mappings, using
 * the same `extractLn1MappedTypes` the bank matrix uses. Yields grouped
 * category columns for normal mappings plus individual columns (composite
 * keys like "service:normal:5") for bank_level / individual mappings.
 *
 * Module-level and pure so `loadAdapter` can call it with the keys it has
 * just deserialized. The `adapterMappedTypes` memo below reads component
 * state, which inside `loadAdapter` still holds the *previous* adapter — the
 * columns must come from the incoming data, not the render-lagged state.
 */
export function computeAdapterMappedTypes(
  protocolType: string,
  ln1KeyMappings: Ln1KeyMappingRow[],
  ln1IntraContentFlags: Record<string | number, unknown>,
  offsetMappingSource: unknown,
): MappedMessageType[] {
  if (!isLn1FamilyProtocol(protocolType)) return [];
  const apiKeys = ln1KeyMappings
    .filter((row) => !row.is_pair_secondary)
    .map((row) => {
      const parsed = parseCompositeKey(row.ln2MessageKey);
      const mc = parsed ? categoryKeyToMessageContent(parsed.categoryKey) : MessageContent.NONE;
      return {
        messageContent: mc,
        message_content: mc,
        messageValue: parsed?.enumValue ?? 0,
        message_value: parsed?.enumValue ?? 0,
        bankLevel: row.bank_level ?? false,
        bank_level: row.bank_level ?? false,
        individual: row.individual ?? false,
        groupShared: row.group_shared ?? false,
        group_shared: row.group_shared ?? false,
        contentType: row.contentType === "bit" ? 1 : 2,
        content_type: row.contentType === "bit" ? 1 : 2,
        floorsBased: row.floors_based,
        floors_based: row.floors_based,
        unitsBased: row.units_based,
        units_based: row.units_based,
        byteOffset: row.byte_offset,
        byte_offset: row.byte_offset,
      };
    });
  // Per-MessageContent intra-content flags — only flagged categories merge.
  // `ln1IntraContentFlags` is shaped `{ [mc: number]: boolean[] }`; any
  // truthy entry in the array marks the MessageContent as flagged.
  const intraFlagsByMc: Record<number, boolean> = {};
  Object.entries(ln1IntraContentFlags).forEach(([k, v]) => {
    const mc = Number(k);
    if (!Number.isFinite(mc) || mc <= 0) return;
    const arr = v as boolean[] | undefined;
    intraFlagsByMc[mc] = Boolean(arr?.some(Boolean));
  });
  return extractLn1MappedTypes(
    { keys: apiKeys, offset_mapping: offsetMappingSource },
    intraFlagsByMc,
  );
}

/**
 * Project mapped types onto the descriptor shape `deserializeOffsetMapping`
 * uses to route legacy friendly-name sub_types ("EnergizeCarCall") to the
 * column that actually represents them. Returns undefined when there are no
 * columns, which keeps the deserializer on its back-compat path.
 */
export function toAdapterColumnDescriptors(
  types: MappedMessageType[],
): AdapterColumnDescriptor[] | undefined {
  if (types.length === 0) return undefined;
  return types.map((t) => ({
    key: t.categoryKey,
    label: t.label,
    isIndividual: Boolean(t.isIndividual),
  }));
}

/**
 * Bank-level inference for an Offset Matrix column. A column is considered
 * bank-level when every stored unit shares the same non-zero offset — there
 * is no meaningful per-car delta. Two sources feed this:
 *
 *  1. Grouped category column (e.g. "control"): its backing offsets array
 *     in `ln1UnitOffsets[mc]` has length ≥ 2 with all equal non-zero values.
 *  2. Individual composite column (e.g. "service:normal:5"): its `[initial,
 *     initial+delta]` pair in `ln1IndividualOffsets[colKey]` stores equal
 *     non-zero values, matching what serialization writes when every unit
 *     carries the same offset.
 *
 * The non-zero guard prevents an absent-or-zero category (e.g. an unconfigured
 * "status" column with no backing data) from falsely inferring bank-level.
 * Exported for testability.
 */
export function isColumnBankLevel(
  colKey: string,
  ln1UnitOffsets: Partial<Record<number, number[]>>,
  ln1IndividualOffsets: Partial<Record<string, [number, number]>> | undefined,
): boolean {
  if (colKey.includes(":")) {
    const pair = ln1IndividualOffsets?.[colKey];
    if (!pair) return false;
    if (pair[0] === 0) return false;
    return pair[0] === pair[1];
  }
  const mc = categoryKeyToMessageContent(colKey);
  if (mc === MessageContent.NONE) return false;
  const arr = ln1UnitOffsets[mc];
  if (!arr || arr.length < 2) return false;
  const first = arr[0];
  if (first === 0) return false;
  return arr.every((v) => v === first);
}

/**
 * Pure column-build math for the AdapterOffsetMatrix. Decides each column's
 * `bankLevel` by precedence:
 *   1. Explicit `mt.bankLevel` from the MappedMessageType (set on individual
 *      columns coming from keys with bank_level=true OR explicitly false).
 *   2. Grouped category override: when `mt.groupedUnitsBasedDelta` is set,
 *      the column has an implicit per-car delta encoded in the keys' byte
 *      sizes (e.g. Kone_M21 STATUS/USAGE/SERVICE/FAULT/GROUPSUPERVISORY all
 *      use units_based keys). Force `bankLevel=false` so the Car Delta input
 *      stays editable, regardless of whether `ln1UnitOffsets` repeats the
 *      same offset across units.
 *   3. Offset-array inference via `isColumnBankLevel` for columns where no
 *      explicit signal exists — preserves legacy behavior for grouped
 *      categories whose member keys are non-units-based.
 *
 * Return value:
 *   - LN1-family: an array (possibly empty) derived strictly from
 *     `adapterMappedTypes`. An empty `adapterMappedTypes` produces `[]`, which
 *     instructs the matrix component to render zero columns — a freshly
 *     created adapter has no offsets to configure until the user adds a key
 *     mapping (grouped MessageContent) or toggles bank_level / individual on
 *     a row (individual composite column).
 *   - MODBUS: an array (possibly empty) derived from `modbusRegTypes`. Only
 *     register types (COIL/DI/HR/IR) that have at least one read or write
 *     configured produce a column. A freshly created MODBUS adapter with
 *     no reads/writes renders zero columns; columns appear incrementally
 *     as the user adds register operations.
 *   - Other non-LN1 / non-MODBUS protocols: `undefined` so the matrix
 *     component falls back to its protocol-based default column set.
 *
 * Exported for direct unit testing without React.
 */
// Canonical order for MODBUS register-type columns. Columns render in this
// order regardless of insertion order in `modbusRegTypes`.
const MODBUS_REG_TYPE_ORDER: readonly string[] = ["COIL", "DI", "HR", "IR"];

// ModbusReadType → register-space the Function Code targets. Source:
// flatbuffers/generated/config/modbus-read-type.ts. ReadHoldingRegisters
// (FC03)→HR, ReadCoils (FC01)→COIL, ReadDiscreteInputs (FC02)→DI,
// ReadInputRegisters (FC04)→IR.
const MODBUS_READ_TYPE_TO_REG_TYPE: Record<number, string> = {
  [ModbusReadType.ReadHoldingRegisters]: "HR",
  [ModbusReadType.ReadCoils]: "COIL",
  [ModbusReadType.ReadDiscreteInputs]: "DI",
  [ModbusReadType.ReadInputRegisters]: "IR",
};

// ModbusWriteType → register-space the Function Code targets. Writes only
// touch COIL (FC05 single, FC15 multi) and HR (FC06 single, FC16 multi,
// FC22 mask). DI and IR are read-only register spaces.
const MODBUS_WRITE_TYPE_TO_REG_TYPE: Record<number, string> = {
  [ModbusWriteType.WriteSingleRegister]: "HR",
  [ModbusWriteType.WriteSingleCoil]: "COIL",
  [ModbusWriteType.WriteMultipleRegisters]: "HR",
  [ModbusWriteType.WriteMultipleCoils]: "COIL",
  [ModbusWriteType.MaskWriteRegister]: "HR",
};

export function buildAdapterOffsetColumns(
  protocolType: string,
  adapterMappedTypes: MappedMessageType[],
  ln1UnitOffsets: Partial<Record<number, number[]>>,
  ln1IndividualOffsets: Partial<Record<string, [number, number]>> | undefined,
  modbusRegTypes?: Record<string, string>,
): AdapterOffsetColumn[] | undefined {
  if (protocolType.toUpperCase() === "MODBUS") {
    // Distinct register types actually present in this adapter's reads/writes.
    // Render one column per type, in canonical order, with the type-string as
    // both label and key (matching the matrix component's MODBUS_COLUMNS shape).
    const usedRegTypes = new Set<string>(Object.values(modbusRegTypes ?? {}));
    return MODBUS_REG_TYPE_ORDER.filter((rt) => usedRegTypes.has(rt)).map((rt) => ({
      key: rt,
      label: rt,
    }));
  }

  if (!isLn1FamilyProtocol(protocolType)) return undefined;

  const bankLevelByKey = new Map<string, boolean>();
  adapterMappedTypes.forEach((mt) => {
    if (mt.bankLevel !== undefined) {
      bankLevelByKey.set(mt.categoryKey, mt.bankLevel);
    } else if (mt.groupedUnitsBasedDelta !== undefined && mt.groupedUnitsBasedDelta > 0) {
      bankLevelByKey.set(mt.categoryKey, false);
    }
  });

  const source: AdapterOffsetColumn[] = adapterMappedTypes.map((mt) => ({
    key: mt.categoryKey,
    label: mt.label,
  }));

  return source.map((col) => {
    const explicit = bankLevelByKey.get(col.key);
    if (explicit !== undefined) {
      return { ...col, bankLevel: explicit };
    }
    return {
      ...col,
      bankLevel: isColumnBankLevel(col.key, ln1UnitOffsets, ln1IndividualOffsets),
    };
  });
}

/**
 * Derive the Offset Matrix value map from CDO state. Extracted from the
 * `adapterOffsetMap` memo below so the Review step can run the *identical*
 * derivation instead of re-implementing it — the divergence this replaces
 * had Review reading offset fields the API never emits, producing phantom
 * overlaps while the editor showed clean.
 *
 * Pure and module-level: same inputs, same map, no component state.
 */
export function buildAdapterOffsetMap(
  protocolType: string,
  adapterMappedTypes: MappedMessageType[],
  adapterOffsetColumns: AdapterOffsetColumn[] | undefined,
  ln1UnitOffsets: Partial<Record<MessageContent, number[]>>,
  ln1IndividualOffsets: Partial<Record<string, [number, number]>> | undefined,
  modbusRegisterOffsets?: {
    initial: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
    delta: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
  },
): AdapterOffsetMap {
  if (isLn1FamilyProtocol(protocolType)) {
    const map: AdapterOffsetMap = {};
    // Key the map by each column's own key so the AdapterOffsetMatrix's
    // `value[col.key]` lookup always finds the stored entry, regardless
    // of spelling variants (e.g. "group_supervisory" vs the alias
    // "group_supervisory_status" used by BankOffsetMatrix).
    // When adapterOffsetColumns is empty/undefined (new adapter with no
    // mappings) the resulting map is empty — there are no columns to
    // populate offsets for, matching the matrix's "render zero columns"
    // contract for `columns: []`.
    const columnKeys: string[] = (adapterOffsetColumns ?? []).map((c) => c.key);
    columnKeys.forEach((colKey) => {
      if (colKey.includes(":")) {
        const pair = ln1IndividualOffsets?.[colKey];
        if (pair) {
          map[colKey] = { initial: pair[0], delta: pair[1] - pair[0] };
        }
        return;
      }
      // For merged (intra-content) columns, take max across every member so
      // a stale non-zero offset on any participating MessageContent remains
      // visible. Single-member columns behave identically to the prior read.
      const mt = adapterMappedTypes.find((m) => m.categoryKey === colKey);
      const memberKeys = mt && mt.categoryKeys.length > 1 ? mt.categoryKeys : [colKey];
      let maxInitial = Number.NEGATIVE_INFINITY;
      let maxDelta = Number.NEGATIVE_INFINITY;
      let hasAny = false;
      memberKeys.forEach((ck) => {
        const mc = categoryKeyToMessageContent(ck);
        const arr = mc !== MessageContent.NONE ? ln1UnitOffsets[mc] : undefined;
        if (!arr) return;
        hasAny = true;
        const pair = deriveInitialAndDelta(arr as number[]);
        if (pair.initial > maxInitial) maxInitial = pair.initial;
        if (pair.delta > maxDelta) maxDelta = pair.delta;
      });
      if (hasAny) {
        map[colKey] = { initial: maxInitial, delta: maxDelta };
      }
    });
    return map;
  }
  if (protocolType.toUpperCase() === "MODBUS") {
    // Only build entries for register types the adapter actually uses
    // (mirrors the column derivation in buildAdapterOffsetColumns). A
    // freshly-created MODBUS adapter with no reads/writes produces an
    // empty map, matching the matrix's "render zero columns" contract.
    //
    // The cdoState's modbusRegisterOffsets uses a strictly-typed key set
    // ("COIL"|"DI"|"HR"|"IR"). The column keys are guaranteed to be in
    // that set (buildAdapterOffsetColumns filters by MODBUS_REG_TYPE_ORDER)
    // — widen to Record<string, number> here so TypeScript doesn't
    // complain about indexing with a generic string key.
    const offsets = modbusRegisterOffsets ?? { initial: {}, delta: {} };
    const initialMap = offsets.initial as Record<string, number>;
    const deltaMap = offsets.delta as Record<string, number>;
    const map: AdapterOffsetMap = {};
    (adapterOffsetColumns ?? []).forEach((col) => {
      map[col.key] = {
        initial: initialMap[col.key] ?? 0,
        delta: deltaMap[col.key] ?? 0,
      };
    });
    return map;
  }
  return {};
}

/**
 * Apply offset_mapping and intra_content_flags to the adapter body ONLY
 * when the user explicitly edited them. Never round-trip loaded values —
 * they may be stale/truncated from prior migrations.
 *
 * Exported for testability. The backend's "only update when len > 0"
 * guard preserves existing data when these fields are omitted.
 */
export function buildOffsetFields(cdoState: {
  ln1UnitOffsets: Record<string, unknown>;
  ln1IndividualOffsets?: Record<string, unknown>;
  ln1IntraContentFlags: Record<string, unknown>;
}): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  const individual = cdoState.ln1IndividualOffsets ?? {};
  const hasGrouped = Object.keys(cdoState.ln1UnitOffsets).length > 0;
  const hasIndividual = Object.keys(individual).length > 0;
  if (hasGrouped || hasIndividual) {
    fields.offset_mapping = serializeOffsetMapping(cdoState.ln1UnitOffsets, individual);
  }
  if (Object.keys(cdoState.ln1IntraContentFlags).length > 0) {
    fields.intra_content_flags = cdoState.ln1IntraContentFlags;
  }
  return fields;
}

/**
 * Extract the leaf enum value from a Go-serialized MessageT union object.
 *
 * Go's FlatBuffer JSON uses PascalCase `{Type, Value}` wrappers for unions,
 * producing deeply nested structures like:
 *   {content: {Type:1, Value: {message: {Type:3, Value: {message: {Type:1, Value: {usage: {Type:1, Value: {message: 10}}}}}}}}}
 *
 * For all categories except ControllerInfo the leaf is `{message: <number>}`.
 * For ControllerInfo the leaf is `{message: {Type: <number>, Value: null}}`.
 *
 * This performs DFS, tracking the deepest `message` field. After traversal the
 * deepest `message` is either the numeric enum or (ControllerInfo) a union
 * wrapper whose `.Type` is the enum.
 */
/**
 * Build a user-readable message from whatever shape `unwrap()` rejects with.
 * Used by both the load (GET) and save (PUT) paths in AdapterEditor — the
 * error envelopes are identical.
 *
 * RTK Query throws `{ status, data }` on HTTP failures where `data` is the
 * parsed response body. The backend's standard error envelope is
 * `{ message: string, status: "error" }`, so the most useful surface is
 * `err.data.message` when present. Falls back through a few other shapes
 * (plain Error, string, generic object → JSON) before the canned fallback,
 * so the user is never shown the useless `String({}) === "[object Object]"`
 * placeholder the prior code emitted.
 *
 * The HTTP status code is appended when available because the difference
 * between a 404 ("adapter doesn't exist server-side") and a 5xx
 * ("backend crashed") is critical context the message field rarely conveys.
 *
 * `fallback` lets each call site override the "nothing usable in the err"
 * default — load paths prefer "Failed to load adapter data", save paths
 * prefer "Save failed — check your connection and try again".
 */
export function formatAdapterApiError(err: unknown, fallback?: string): string {
  const FALLBACK = fallback ?? "Save failed — check your connection and try again";

  if (err === null || err === undefined) return FALLBACK;

  if (typeof err === "string") return err || FALLBACK;

  if (err instanceof Error) return err.message || FALLBACK;

  if (typeof err !== "object") return FALLBACK;

  const e = err as Record<string, unknown>;
  const status = typeof e.status === "number" ? e.status : null;
  const statusSuffix = status !== null ? ` (HTTP ${status})` : "";

  const data = e.data;
  if (typeof data === "string" && data) return `${data}${statusSuffix}`;
  if (data !== null && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (typeof d.message === "string" && d.message) {
      return `${d.message}${statusSuffix}`;
    }
    if (typeof d.error === "string" && d.error) return `${d.error}${statusSuffix}`;
    // Last resort: JSON-stringify the body so the user sees the actual
    // backend response instead of "[object Object]".
    try {
      return `${JSON.stringify(d)}${statusSuffix}`;
    } catch {
      return FALLBACK;
    }
  }

  if (typeof e.error === "string" && e.error) return `${e.error}${statusSuffix}`;
  if (typeof e.message === "string" && e.message) return `${e.message}${statusSuffix}`;

  return FALLBACK;
}

function extractEnumFromGoMember(obj: unknown): number | null {
  let deepest: unknown;
  function walk(node: unknown): void {
    if (typeof node !== "object" || node === null) return;
    const rec = node as Record<string, unknown>;
    Object.keys(rec).forEach((key) => {
      if (key === "message") deepest = rec[key];
      if (typeof rec[key] === "object" && rec[key] !== null) walk(rec[key]);
    });
  }
  walk(obj);
  if (typeof deepest === "number") return deepest;
  if (
    typeof deepest === "object" &&
    deepest !== null &&
    typeof (deepest as Record<string, unknown>).Type === "number"
  ) {
    return (deepest as Record<string, unknown>).Type as number;
  }
  return null;
}

const isValidSemver = (ver: string): boolean => {
  if (!ver) return false;
  return /^\d+\.\d+\.\d+(-[\w.]+)?(\+[\w.]+)?$/.test(ver);
};

/**
 * Normalize an offset/flag map from the API into MessageContent-keyed form.
 * Handles both legacy string keys ("status") and numeric MessageContent keys.
 */
export function normalizeOffsetMap<T>(raw: Record<string, T>): Partial<Record<MessageContent, T>> {
  // Guard: if raw is an array (the adapter's offset_mapping format),
  // return empty. Array-format offset_mapping uses positional entries
  // [{offset, unit_number, message_type}, ...] which cannot be
  // normalized to MessageContent keys. Returning empty prevents
  // buildOffsetFields from including truncated data in the save body.
  if (Array.isArray(raw)) return {};

  const result: Partial<Record<MessageContent, T>> = {};
  const numericResult = result as Partial<Record<number, T>>;
  Object.entries(raw).forEach(([key, value]) => {
    const numKey = Number(key);
    if (!Number.isNaN(numKey) && numKey > 0 && numKey <= 9) {
      numericResult[numKey] = value;
    } else {
      const mc = categoryKeyToMessageContent(key);
      if (mc !== MessageContent.NONE) {
        numericResult[mc as number] = value;
      }
    }
  });
  return result;
}

const INITIAL_CDO_STATE: CdoStateBundle = {
  cdoMce: { ...BLANK_ADAPTER_CDO_MCE },
  cdoLn1: { ...BLANK_ADAPTER_CDO_LN1 },
  cdoModbus: {
    reads: [],
    writes: [],
    slaveId: 1,
    pollingInterval: BigInt(1000),
  },
  cdoOpc: { ...BLANK_ADAPTER_CDO_OPC },
  cdoOpcUa: { ...BLANK_ADAPTER_CDO_OPCUA },
  cdoLobbyVision2: { ...BLANK_ADAPTER_CDO_LOBBYVISION2 },
  cdoSequent: { ...BLANK_ADAPTER_CDO_SEQUENT },
  cdoBacnet: [],
  bacnetBankMappings: [],
  cdoJsonFallback: "{}",
  ln1KeyMappings: [],
  ln1UnitOffsets: {},
  ln1IntraContentFlags: {},
  ln1IndividualOffsets: {},
  modbusRegisterOffsets: { initial: {}, delta: {} },
};

/** Extract encoded type, message content, and message value from a key mapping row. */
function resolveMessageFields(
  row: Ln1KeyMappingRow,
): { encodedType: number; messageContent: number; messageValue: number } | null {
  const parsed = parseCompositeKey(row.ln2MessageKey);
  if (!parsed) return null;
  const mc = categoryKeyToMessageContent(parsed.categoryKey);
  if (mc === MessageContent.NONE) return null;
  const category = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === parsed.categoryKey);
  if (!category) return null;
  return {
    encodedType: category.encodedMessageContent,
    messageContent: mc,
    messageValue: parsed.enumValue,
  };
}

/**
 * Encode the value object for a key mapping into UTF-8 bytes.
 * The backend stores the value as a JSON blob inside valueBinary.
 * This mirrors the JSON string that buildKeyRequestBody puts in the
 * `value` field, which normalizeKeyRecord later decodes via TextDecoder.
 */
function encodeValueBinary(mapping: Ln1KeyMappingRow): number[] {
  const valueObj =
    mapping.contentType === "bit"
      ? {
          byte_offset: mapping.byte_offset,
          bit_offset: mapping.bit_offset,
          data: mapping.data,
        }
      : { offset: mapping.offset, data: mapping.value };
  const encoded = new TextEncoder().encode(JSON.stringify(valueObj));
  return Array.from(encoded);
}

/**
 * Convert an Ln1KeyMappingRow into an Archive.KeyT FlatBuffer object.
 *
 * Exported for testability. Mirrors buildKeyRequestBody but populates
 * a KeyT directly instead of a plain JSON body.
 *
 * For CREATE operations the caller must NOT set KeyT.id — this function
 * sets a zero UUID placeholder (server ignores it on create). For UPDATE
 * operations the caller overwrites keyT.id after calling this function.
 *
 * Returns null if the row cannot be resolved to a valid message triple.
 */
export function ln1RowToKeyT(
  row: Ln1KeyMappingRow,
  adapterId: string,
  deviceType: string,
  secondary?: Ln1KeyMappingRow,
): KeyT | null {
  const primaryMsg = resolveMessageFields(row);
  if (!primaryMsg) return null;

  const contentType: number = row.contentType === "bit" ? 1 : 2;

  // Resolve fbs_device_type from the string deviceType.
  const resolvedDeviceType =
    LiftType[deviceType.toUpperCase() as keyof typeof LiftType] ?? LiftType.ELEVATOR;

  // Assignment style, not KeyT's positional constructor. `Archive.Key` grows by
  // appending — its own schema says "Appended at the end of the table to
  // preserve FlatBuffer wire compatibility — never insert fields in the middle"
  // — so every new field lands in the trailing position. Every generated *T
  // parameter carries a default, so a call that stops short of the new arity
  // still compiles and silently writes that default. That is precisely how
  // `groupShared` (field #24) was dropped from `buildKeyT` in
  // requestSerializers.ts, and this was the last positional `new KeyT(...)`
  // left. createdAt/updatedAt/deletedAt are intentionally unassigned: KeyT
  // defaults them to null, identical to the previous explicit `null` arguments.
  const keyT = new KeyT();
  keyT.id = EMPTY_UUID;
  keyT.adapterId = stringToUuidT(adapterId);
  keyT.fbsDeviceType = resolvedDeviceType;
  keyT.encodedType = primaryMsg.encodedType;
  keyT.messageContent = primaryMsg.messageContent;
  keyT.messageValue = primaryMsg.messageValue;
  keyT.contentType = contentType;
  keyT.position = 0;
  // The lower_* group stays zeroed unless a secondary row resolves below.
  keyT.lowerEncodedType = 0;
  keyT.lowerMessageContent = 0;
  keyT.lowerMessageValue = 0;
  keyT.lowerFbsDeviceType = LiftType.ELEVATOR;
  keyT.lowerPosition = 0;
  keyT.floorsBased = row.floors_based;
  keyT.unitsBased = row.units_based;
  keyT.bankLevel = row.bank_level ?? false;
  keyT.debounceTime = row.debounce_time ?? 0;
  keyT.floorIndexing = null;
  keyT.valueBinary = encodeValueBinary(row);
  keyT.individual = row.individual ?? false;
  keyT.groupShared = row.group_shared ?? false;
  // Absent reads as BOTH, matching the column, the wire and the server. A
  // paired secondary row is part of THIS key (it populates the lower_* group
  // above), so it cannot carry a direction of its own — the primary's applies
  // to the whole key.
  keyT.direction = row.direction ?? KeyDirection.BOTH;

  if (secondary) {
    const lowerMsg = resolveMessageFields(secondary);
    if (lowerMsg) {
      keyT.lowerEncodedType = lowerMsg.encodedType;
      keyT.lowerMessageContent = lowerMsg.messageContent;
      keyT.lowerMessageValue = lowerMsg.messageValue;
      keyT.lowerFbsDeviceType = resolvedDeviceType;
    }
  }

  return keyT;
}

// ************************************************ */
// KEY DESERIALIZATION **************************** */

/**
 * Normalize an API key record from either format:
 * - FlatBuffer KeyT (camelCase, valueBinary as number[])
 * - JSON fallback (snake_case, value as JSON string)
 *
 * Returns a uniform snake_case shape for downstream processing.
 */
function normalizeKeyRecord(k: any): any {
  // Detect FlatBuffer KeyT by checking for camelCase fields.
  // JSON fallback has snake_case (content_type), FlatBuffer has camelCase (contentType).
  const isFlatBuffer = k.contentType !== undefined && k.content_type === undefined;
  if (!isFlatBuffer) return k;

  // Decode valueBinary (number[] or Uint8Array containing JSON bytes) to a value string
  let valueStr: string | undefined;
  if (k.valueBinary && (k.valueBinary.length > 0 || k.valueBinaryLength?.() > 0)) {
    try {
      const bytes =
        k.valueBinary instanceof Uint8Array ? k.valueBinary : new Uint8Array(k.valueBinary);
      valueStr = new TextDecoder().decode(bytes);
    } catch {
      // keep undefined
    }
  }

  return {
    id: k.id,
    adapter_id: k.adapterId,
    content_type: k.contentType,
    encoded_type: k.encodedType,
    message_content: k.messageContent,
    message_value: k.messageValue,
    position: k.position,
    fbs_device_type: k.fbsDeviceType,
    floors_based: k.floorsBased,
    units_based: k.unitsBased,
    bank_level: k.bankLevel,
    individual: k.individual,
    group_shared: k.groupShared ?? k.group_shared,
    debounce_time: k.debounceTime,
    floor_indexing: k.floorIndexing,
    lower_encoded_type: k.lowerEncodedType,
    lower_message_content: k.lowerMessageContent,
    lower_message_value: k.lowerMessageValue,
    lower_fbs_device_type: k.lowerFbsDeviceType,
    lower_position: k.lowerPosition,
    value: valueStr,
    pair_id: k.pairId ?? k.pair_id,
    is_pair_secondary: k.isPairSecondary ?? k.is_pair_secondary,
    // MUST be carried. GET /keys/ answers FlatBuffers unconditionally
    // (writePaginatedKeys has no Accept negotiation and no JSON branch), so
    // this literal is the ONLY shape the editor ever sees. Omitting direction
    // did not merely hide it: deserializeApiKeys got undefined, the Direction
    // select fell back to BOTH, and the next save shipped
    // `row.direction ?? KeyDirection.BOTH` for every row — actively
    // overwriting a correctly stored TO_DEVICE back to BOTH in the database.
    // So a value the backend had persisted correctly was destroyed by the act
    // of reopening the editor and saving anything at all.
    //
    // It went unnoticed because every fixture in AdapterEditor.serialization
    // .test.ts is snake_case, which takes the `!isFlatBuffer` early return
    // above and never exercises this branch.
    direction: k.direction,
  };
}

function parseKeyValueBlob(
  k: any,
  contentType: "bit" | "byte",
): { byteOffset: number; bitOffset: number; data: boolean; offset: number; byteValue: number } {
  let byteOffset = 0;
  let bitOffset = 0;
  let data = false;
  let offset = 0;
  let byteValue = 0;
  try {
    const val = typeof k.value === "string" ? JSON.parse(k.value) : k.value;
    if (val) {
      if (contentType === "bit") {
        byteOffset = val.byte_offset ?? 0;
        bitOffset = val.bit_offset ?? 0;
        data = val.data ?? false;
      } else {
        // ByteMessage: canonical format is {offset, data}. Old orgs may
        // have BitMessage-shaped JSON with byte_offset/bit_offset.
        offset = val.offset ?? val.byte_offset ?? 0;
        byteValue = val.data ?? val.bit_offset ?? 0;
      }
    }
  } catch {
    // keep defaults
  }
  return { byteOffset, bitOffset, data, offset, byteValue };
}

/**
 * Transform API Key records into Ln1KeyMappingRow objects.
 *
 * Handles both FlatBuffer KeyT (camelCase) and JSON fallback (snake_case)
 * response formats. Paired bit rows stored as a single API key with lower_*
 * fields are reconstructed into two rows.
 */
export function deserializeApiKeys(apiKeys: any[]): Ln1KeyMappingRow[] {
  return apiKeys.reduce<Ln1KeyMappingRow[]>((rows, raw) => {
    const k = normalizeKeyRecord(raw);
    const contentType: "bit" | "byte" = k.content_type === 1 ? "bit" : "byte";

    // Reconstruct composite key from message_content (1-based) + message_value
    const catIndex = (k.message_content ?? 1) - 1;
    const category = LN2_MESSAGE_CATEGORIES[catIndex];
    if (!category) return rows;
    const categoryKey = category.categoryKey;
    const ln2MessageKey = `${categoryKey}:normal:${k.message_value ?? 0}`;

    const { byteOffset, bitOffset, data, offset, byteValue } = parseKeyValueBlob(k, contentType);

    const normalizedId = k.id && typeof k.id === "object" ? formatUuidT(k.id) : (k.id ?? "");
    const primaryRow: Ln1KeyMappingRow = {
      id: normalizedId || undefined,
      ln2MessageKey,
      contentType,
      byte_offset: byteOffset,
      bit_offset: bitOffset,
      data,
      offset,
      value: byteValue,
      units_based: k.units_based ?? false,
      floors_based: k.floors_based ?? false,
      bank_level: k.bank_level ?? false,
      individual: k.individual ?? false,
      // Persisted flag OR the category default (on for the group-scoped
      // categories) so existing data isn't falsely flagged before a re-save.
      group_shared: (k.group_shared ?? false) || isGroupScopedCategory(categoryKey),
      debounce_time: k.debounce_time ?? null,
      pair_id: k.pair_id ?? null,
      is_pair_secondary: k.is_pair_secondary ?? false,
      // Accepts both spellings: the FlatBuffer path yields `direction`, the
      // JSON path the same name. A key from an API that predates the field
      // stays undefined, which reads as BOTH everywhere downstream.
      direction: (k.direction ?? undefined) as KeyDirection | undefined,
    };

    // If lower_message_content is set, reconstruct the secondary (paired) row
    if (k.lower_message_content != null) {
      const lowerCatIndex = (k.lower_message_content as number) - 1;
      const lowerCategory = LN2_MESSAGE_CATEGORIES[lowerCatIndex];
      if (lowerCategory) {
        const lowerCategoryKey = lowerCategory.categoryKey;
        const lowerLn2MessageKey = `${lowerCategoryKey}:normal:${k.lower_message_value ?? 0}`;
        const secondaryRow: Ln1KeyMappingRow = {
          ln2MessageKey: lowerLn2MessageKey,
          contentType: "bit",
          byte_offset: byteOffset,
          bit_offset: bitOffset,
          data: !data,
          offset: 0,
          value: 0,
          units_based: primaryRow.units_based,
          floors_based: primaryRow.floors_based,
          bank_level: primaryRow.bank_level,
          individual: primaryRow.individual,
          // A pair is ONE stored key (the secondary populates lower_*), so it
          // physically cannot carry a direction of its own.
          direction: primaryRow.direction,
          group_shared: primaryRow.group_shared,
          debounce_time: primaryRow.debounce_time,
          pair_id: primaryRow.pair_id,
          is_pair_secondary: true,
        };
        return [...rows, primaryRow, secondaryRow];
      }
    }

    return [...rows, primaryRow];
  }, []);
}

// ************************************************ */
// BANK CONTEXT TYPE ****************************** */
export interface BankContextInfo {
  bankId: string;
  bankName: string;
  lifts: { id: string; name: string }[];
}

// ************************************************ */
// EDIT-DIFF SNAPSHOT ****************************** */
//
// Snapshot of the adapter's wizard-relevant fields captured AFTER
// loadAdapter hydrates the form and BEFORE the user makes any edits.
// On save, the editor compares this against the current state and
// forwards the names of changed fields through onSaveSuccess so the
// wizard parent (StepAdapters) can decide which downstream wizard
// steps to invalidate. Field names here match the keys in
// ADAPTER_FIELD_RULES (utils/wizardInvalidation.ts) so the parent
// can pass them straight into stepsForChangedFields.
//
// JSON-stringifying cdoState (minus oneHotExclusion) collapses the
// CDO object's many sub-fields (cdoMce, cdoLn1, cdoModbus, ...) into
// a single "did anything in the CDO change?" flag, which maps to
// ADAPTER_FIELD_RULES.cdoBinary. oneHotExclusion is split out
// because its rule (Notifications/Actions only) is narrower than
// cdoBinary's (Banks + Notifications + Actions).
interface AdapterEditSnapshot {
  name: string;
  protocolType: string;
  active: boolean;
  cdoSignature: string;
  oneHotExclusionSignature: string;
}

/** JSON.stringify with BigInt support — cdoModbus.pollingInterval is a BigInt. */
function stableJson(v: unknown): string {
  return JSON.stringify(v, (_key, value) => (typeof value === "bigint" ? value.toString() : value));
}

function buildAdapterSnapshot(args: {
  name: string;
  protocolType: string;
  active: boolean;
  cdoState: CdoStateBundle;
}): AdapterEditSnapshot {
  const { oneHotExclusion, ...cdoRest } = args.cdoState as CdoStateBundle & {
    oneHotExclusion?: unknown;
  };
  return {
    name: args.name,
    protocolType: args.protocolType,
    active: args.active,
    cdoSignature: stableJson(cdoRest),
    oneHotExclusionSignature: stableJson(oneHotExclusion ?? null),
  };
}

/** Returns the names of fields that differ between two snapshots. */
function diffAdapterSnapshots(
  before: AdapterEditSnapshot | null,
  after: AdapterEditSnapshot,
): string[] {
  if (!before) return [];
  const changed: string[] = [];
  if (before.name !== after.name) changed.push("name");
  if (before.protocolType !== after.protocolType) changed.push("protocolType");
  if (before.active !== after.active) changed.push("active");
  if (before.cdoSignature !== after.cdoSignature) changed.push("cdoBinary");
  if (before.oneHotExclusionSignature !== after.oneHotExclusionSignature) {
    changed.push("oneHotExclusion");
  }
  return changed;
}

// ************************************************ */
// PROPS ****************************************** */
export interface AdapterEditorProps {
  mode: "wizard" | "drawer" | "page";
  existingAdapterId?: string;
  formAction: "create" | "edit" | "duplicate";
  /**
   * Fired after a successful save (POST or PUT).
   *
   * @param changedFields  For PUT (edit) only: names of fields that
   *   actually changed since loadAdapter populated the form. The
   *   wizard parent feeds these into stepsForChangedFields to decide
   *   which downstream wizard steps to invalidate, so a cosmetic-
   *   only adapter rename does not force the user to re-walk
   *   Banks/Lifts/etc. POST (create) saves pass `undefined` here
   *   because every field is "new" — the parent treats that as the
   *   classic post-create completion and runs its own logic.
   */
  onSaveSuccess?: (
    adapterId: string,
    adapterName: string,
    protocolType: string,
    verified?: boolean,
    changedFields?: string[],
    validationMessage?: string,
  ) => void;
  onCancel?: () => void;
  bankContext?: BankContextInfo;
}

// ************************************************ */
// COMPONENT ************************************** */
/**
 * Everything the Max Cars projection reads. Gathered into one struct so the
 * assembly itself is a pure function that can be driven directly, rather than
 * glue that only exists inside a component and can therefore only be exercised
 * by accident.
 */
export interface AdapterMaxCarsInputs {
  columns: AdapterOffsetColumn[];
  mappedTypes: MappedMessageType[];
  keyMappings: MatrixKeyMappingSubset | undefined;
  floors: number | undefined;
  regTypes: CategoryRegTypeMap | undefined;
  offsetMap: AdapterOffsetMap;
  storedMapping: OffsetMappingEntry[];
}

/**
 * Project the largest car count this adapter's offsets can serve.
 *
 * TWO SOURCES, and neither subsumes the other.
 *
 * The authored matrix is live and looks FORWARD past the car count the adapter
 * currently enumerates, which is the question an operator raising the car count
 * is really asking. It is also lossy: it carries one column per category the
 * editor knows about and folds a category's variants together by taking the
 * maximum initial and delta across them.
 *
 * The stored mapping is faithful — every per-variant series the LN1 importer
 * wrote, byte for byte the table LiftCloud enforces the cap on — but it stops
 * where the enumeration stops. On the real six-car site the two series that
 * contest byte 200 are variants with no matrix column at all, which is why a
 * projection reading only the matrix cannot see the collision this feature is
 * named after.
 *
 * The tighter bound wins, because satisfying only the looser one leaves the
 * other's collision live.
 */
/**
 * True when this payload speaks the LN1 importer's numbering rather than the
 * schema's.
 *
 * The same one-pass discriminator `deserializeOffsetMapping` runs, and
 * deliberately the same one: two answers to "which namespace is this
 * `message_type` in" is precisely the defect being closed here. A payload is
 * legacy if ANY row betrays it — a `message_type` of 0, which the schema does
 * not use, or a `message_sub_type` that is a friendly name rather than a
 * variant — and a mixed payload counts as legacy so its legacy half routes
 * correctly.
 */
function mappingPayloadIsLegacy(rows: readonly OffsetMappingRow[]): boolean {
  return rows.some((row) => {
    if (row.message_type === 0) return true;
    const sub = row.message_sub_type;
    return typeof sub === "string" && sub !== "" && sub !== "normal" && sub !== "valued";
  });
}

/**
 * `getDisplayName`, with its "I could not name this" sentinel turned back into
 * an absence.
 *
 * It answers `"--"` for a key it cannot resolve, which is the right thing in a
 * table cell and the wrong thing here: a resolved label REPLACES the wire
 * spelling, so returning the sentinel would put `byte 200 — -- car 4 vs -- car
 * 3` in front of an operator. Null instead, and the wire spelling — which at
 * least matches what the server's own collision log prints — survives.
 */
function resolvedDisplayName(compositeKey: string): string | null {
  const name = getDisplayName(compositeKey);
  return name === "--" ? null : name;
}

/** The operator-facing name of a category, e.g. "Traffic". */
function categoryLabelOf(categoryKey: string | undefined): string | undefined {
  if (categoryKey === undefined) return undefined;
  return LN2_MESSAGE_CATEGORIES.find((category) => category.categoryKey === categoryKey)?.label;
}

/**
 * Both spellings of a category key, longest-standing split first.
 *
 * The offset matrix keys Group Supervisory Status' column
 * "group_supervisory_status"; ln2MessageOptions' canonical categoryKey is
 * "group_supervisory". CATEGORY_KEY_ALIASES documents the split and resolves it
 * in one direction only, so a key resolved from a MessageContent has to be
 * offered to the matrix under both names or that one category silently fails to
 * find its own column — which is precisely the class of miss this resolver
 * exists to end.
 */
function matrixSpellingsOf(categoryKey: string): string[] {
  return categoryKey === "group_supervisory"
    ? [categoryKey, "group_supervisory_status"]
    : [categoryKey];
}

/**
 * Teach the enumerated-mapping projection what the offset matrix already knows
 * about the same registers.
 *
 * WHY THIS EXISTS AT ALL. `maxCarsProjection` may not import the category tables
 * or the generated enums — its header says so, and that is what keeps it a pure
 * module the sweep can be driven through directly — so it reads a mapping row as
 * an opaque `type|subtype|value` triple. That is survivable for the byte
 * arithmetic and wrong for everything else:
 *
 *   - `message_type` is NOT a MessageContent on a legacy payload. The importer's
 *     7 is Traffic; MessageContent 7 is Group Supervisory Status. The six-car
 *     site's car-position series — the one this whole feature is named after —
 *     was carrying the discriminant of a category it has nothing to do with;
 *   - `CategoryRegTypeMap` is keyed by COLUMN key, so the MODBUS register-type
 *     exemption could never fire on a series keyed by the wire triple, whatever
 *     the operator declared;
 *   - the matrix honours an explicit `groupShared: false` while the mapping
 *     could only guess from whether the offsets happen to vary, so one adapter
 *     answered one question about one register two different ways;
 *   - and the operator was shown `message_type 7:P:-` for something the editor
 *     was calling "Traffic: P" two panels away.
 *
 * WHAT DOES NOT RESOLVE, AND WHY THAT IS CORRECT. A legacy friendly-name variant
 * ("UpAssignedRearHallCall") has no matrix column, and that absence is the
 * entire reason a second source exists. So it resolves a category and a label
 * and returns `colKey: null` rather than inventing a column to hang a register
 * type on; its sharing policy then falls back to the data, which is what this
 * source has always done for exactly these series.
 */
export function buildMappingSeriesResolver(
  rows: readonly OffsetMappingRow[],
  columns: MatrixColumnSpec[],
): MappingSeriesResolver {
  const legacy = mappingPayloadIsLegacy(rows);
  const byColKey = new Map(columns.map((col) => [col.colKey, col]));

  /**
   * The column this key names, under whichever spelling the matrix used, plus
   * what the projection is allowed to learn from it. `undefined` for both
   * fields is the honest answer when no column matched — the mapping source
   * then keeps its data heuristic, as it always has for the per-variant series
   * the matrix has no column for.
   */
  const fromColumn = (candidates: string[]) => {
    const col = candidates.map((key) => byColKey.get(key)).find((found) => found !== undefined);
    return {
      colKey: col?.colKey ?? null,
      regType: col?.regType,
      sharedAcrossCars: col === undefined ? undefined : isSharedAcrossCars(col),
    };
  };

  return (row: OffsetMappingRow): MappingSeriesIdentity | null => {
    const sub = row.message_sub_type ?? "";

    // A canonical variant row reads canonically even inside a legacy payload,
    // which is exactly what matchLegacyEntryToColumn already does for it.
    if ((sub === "normal" || sub === "valued") && typeof row.message_value === "number") {
      const categoryKey = messageContentToCategoryKey(row.message_type as MessageContent);
      if (categoryKey === undefined) return null;
      const compositeKey = `${categoryKey}:${sub}:${row.message_value}`;
      const matched = fromColumn(
        matrixSpellingsOf(categoryKey).map((spelling) => `${spelling}:${sub}:${row.message_value}`),
      );
      return {
        ...matched,
        // The column key is the join; the composite key is what it WOULD be even
        // when no column exists, and naming it is still better than the triple.
        colKey: matched.colKey ?? compositeKey,
        mcNumber: row.message_type,
        label: resolvedDisplayName(compositeKey),
      };
    }

    const mcNumber = legacy ? remapLegacyMessageType(row.message_type) : row.message_type;
    const categoryKey = messageContentToCategoryKey(mcNumber as MessageContent);
    const categoryLabel = categoryLabelOf(categoryKey);
    if (categoryKey === undefined || categoryLabel === undefined) return null;

    // An empty sub_type is the whole category, and that IS a matrix column.
    if (sub === "") {
      const matched = fromColumn(matrixSpellingsOf(categoryKey));
      return { ...matched, colKey: matched.colKey ?? categoryKey, mcNumber, label: categoryLabel };
    }

    // Friendly-name variant: category known, column genuinely absent.
    return { colKey: null, mcNumber, label: `${categoryLabel}: ${sub}` };
  };
}

export function projectAdapterMaxCars(inputs: AdapterMaxCarsInputs): MaxCarsProjection {
  const specs = buildMatrixColumnSpecs(
    inputs.columns,
    inputs.mappedTypes,
    inputs.keyMappings,
    inputs.floors,
    inputs.regTypes,
  );

  return combineMaxCarsProjections(
    projectMaxCars(specs, inputs.offsetMap, inputs.regTypes),
    projectMaxCarsFromOffsetMapping(
      inputs.storedMapping,
      buildMappingSeriesResolver(inputs.storedMapping, specs),
    ),
  );
}

/** One line of the Max Cars caption: what to say, and how alarming it is. */
export interface MaxCarsCaptionLine {
  text: string;
  tone: "warning" | "muted";
}

const MAX_CARS_TONE: Record<MaxCarsCaptionLine["tone"], string> = {
  warning: "#faad14",
  muted: "#8c8c8c",
};

/**
 * Why no cap could be derived, said in terms of the thing the operator must fix.
 *
 * Each of these is a real configuration defect that a cap cannot repair, so
 * auto-detection declines to write one and names the defect instead. Saying
 * nothing here is what turned the previous version's silence into the feature's
 * own failure mode one layer up.
 */
function underivableCapLines(projection: MaxCarsProjection): MaxCarsCaptionLine[] {
  if (projection.zeroDeltaColumns.length > 0) {
    return [
      {
        text:
          `No cap derived: no car delta on ` +
          `${projection.zeroDeltaColumns.map((column) => column.label).join(", ")}. ` +
          `Every car would read the same register — the remedy is a delta, not a cap.`,
        tone: "warning",
      },
    ];
  }

  const bound = projection.bound;
  if (!bound) return [];

  if (projection.safeCarCount === null) {
    return [
      {
        text: `No cap derived: expansion is already bounded on the first car (${describeMaxCarsBound(bound)}).`,
        tone: "warning",
      },
    ];
  }

  if (projection.safeCarCount < MIN_AUTO_FILL_CARS) {
    return [
      {
        text:
          `No cap derived: expansion is bounded at car ${bound.carCount} ` +
          `(${describeMaxCarsBound(bound)}). A cap of ${projection.safeCarCount} is not the ` +
          `remedy for offsets that contest a byte this early — fix the offsets.`,
        tone: "warning",
      },
    ];
  }

  return [];
}

/**
 * Everything the Max Cars box cannot say by itself.
 *
 * The FIRST line is the one this feature owes the operator: a stored cap below
 * the car count the adapter enumerates silently deletes the cars above it, on
 * the server, with a log line nobody reads. That is the same silent truncation
 * the cap exists to prevent, relocated one layer up, and it is disclosed here
 * regardless of where the cap came from or whether anything was auto-detected.
 *
 * Pure and exported so the wording is pinned directly, without a render.
 */
export function maxCarsCaptionLines(
  maxCars: number | null,
  origin: MaxCarsOrigin,
  projection: MaxCarsProjection,
  enumeratedCars: number,
): MaxCarsCaptionLine[] {
  const lines: MaxCarsCaptionLine[] = [];
  const { bound } = projection;

  if (maxCars !== null && enumeratedCars > maxCars) {
    lines.push({
      text:
        `This adapter enumerates ${enumeratedCars} cars and the cap allows ${maxCars}: ` +
        `cars ${maxCars + 1}–${enumeratedCars} will not be generated.`,
      tone: "warning",
    });
  }

  if (
    maxCars !== null &&
    bound !== null &&
    projection.safeCarCount !== null &&
    maxCars > projection.safeCarCount
  ) {
    // The number is NOT changed. Forcing expansion to a chosen count is exactly
    // what typing one is for; the operator is told what will collide and left
    // holding their own value.
    lines.push({
      text:
        `Expansion is bounded at car ${bound.carCount} (${describeMaxCarsBound(bound)}); ` +
        `this cap allows ${maxCars}.`,
      tone: "warning",
    });
  }

  if (maxCars !== null && origin === "auto" && bound !== null) {
    lines.push({
      text:
        `Auto-detected from the offsets: expansion is bounded at car ${bound.carCount} ` +
        `(${describeMaxCarsBound(bound)}). Save to store it.`,
      tone: "muted",
    });
  }

  if (maxCars === null) lines.push(...underivableCapLines(projection));

  if (projection.extentOverlap !== null) {
    // Advisory, and labelled as such: the extent comes from the editor's key
    // mappings and does not exist in the table LiftCloud enforces on, so it
    // never bounds the cap. Reporting it is still worth doing — the two cars
    // read overlapping regions of the frame.
    lines.push({
      text:
        `Category byte ranges also overlap from car ${projection.extentOverlap.carCount} ` +
        `(${describeCarCollision(projection.extentOverlap.witness)}). No wire byte is ` +
        `contested there, so it does not bound the cap.`,
      tone: "muted",
    });
  }

  return lines;
}

function renderMaxCarsCaption(
  maxCars: number | null,
  origin: MaxCarsOrigin,
  projection: MaxCarsProjection,
  enumeratedCars: number,
): React.ReactElement | null {
  const lines = maxCarsCaptionLines(maxCars, origin, projection, enumeratedCars);
  if (lines.length === 0) return null;

  return (
    <div data-testid="adapter-max-cars-caption" style={{ marginTop: 4, fontSize: 12 }}>
      {lines.map((line) => (
        <div key={line.text} style={{ color: MAX_CARS_TONE[line.tone] }}>
          {line.text}
        </div>
      ))}
    </div>
  );
}

export default function AdapterEditor({
  mode,
  existingAdapterId = undefined,
  formAction,
  onSaveSuccess = undefined,
  onCancel = undefined,
  bankContext = undefined,
}: AdapterEditorProps) {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const { theme, userLogged } = useSelector((state: RootState) => state.home);
  // The server shows adapter passwords to a MASTER ADMIN only, deciding by role
  // name (utils.IsMasterAdmin), so this asks the same question: no org-id
  // fallback.
  const callerSeesAdapterSecrets = isMasterAdmin(userLogged);
  // Where the Overlap Analysis report opens. Remembered in this browser, so a
  // commissioning engineer who works with the report on a second monitor is
  // not asked again for every adapter.
  const overlapOpenIn = useSelector((state: RootState) => state.adapter.overlapAnalysisOpenIn);
  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [adapterName, setAdapterName] = useState("");
  const [protocolType, setProtocolType] = useState("");
  const [liftType, setLiftType] = useState("ELEVATOR");
  const [version, setVersion] = useState("1.0.0");
  const [authType, setAuthType] = useState("NONE");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [floors, setFloors] = useState(1);
  const [units, setUnits] = useState(1);
  const [leadingZeroesId, setLeadingZeroesId] = useState("");
  // The two independent floor bases. Position travels as a whole-byte VALUE
  // and calls travel as BITS in a mask, and a controller may number them
  // differently — vendorFloorIndexing can only say 0 or 1 and only ever applied
  // to the value path, so a disagreeing call bitmap had no setting at all.
  const [positionFloorBase, setPositionFloorBase] = useState<number>(0);
  const [callFloorBase, setCallFloorBase] = useState<number>(0);
  // The `vendor_floor_indexing` wire string exactly as the loaded adapter
  // carried it; null until an adapter has loaded (a new adapter carries none).
  // LIFT-1196 makes the server ignore that column for non-LN1 protocols, so
  // a non-LN1 save echoes the value back unchanged instead of deriving it
  // from the position base. The LN1 family keeps the derivation and never
  // reads this.
  const [storedVendorFloorIndexing, setStoredVendorFloorIndexing] = useState<string | null>(null);

  // The expansion cap. NULL is a real, common state meaning "no cap" — it is
  // NOT a missing value to be defaulted to 0, which would mean "cap at zero
  // cars". Every read and write below preserves that distinction.
  const [maxCars, setMaxCars] = useState<number | null>(null);
  /**
   * Where the value in that box came from. Transient, never persisted, never a
   * column.
   *
   * It exists because an EMPTY box has two histories that must not be treated
   * alike: nobody has ever expressed a cap ("unset"), or the operator just
   * cleared one ("manual"). A rule keyed on `maxCars === null` cannot tell them
   * apart, so clearing the field re-populated it on the very next projection and
   * the empty state the tooltip promises was unreachable on any adapter whose
   * offsets project a collision.
   */
  const [maxCarsOrigin, setMaxCarsOrigin] = useState<MaxCarsOrigin>("unset");
  /**
   * The offset mapping as STORED, enumerated per (category, car).
   *
   * This is the table LiftCloud enforces the cap on, and it is not reconstructible
   * from the offset matrix: the matrix carries one column per category and folds a
   * category's variants together, while the stored mapping keeps every variant the
   * LN1 importer wrote. On the real six-car site the two series that contest byte
   * 200 are variants with no matrix column at all.
   */
  const [storedOffsetMapping, setStoredOffsetMapping] = useState<OffsetMappingEntry[]>([]);
  // Vendor Floor Indexing is not separate state: it is the two-choice spelling
  // of the position base, and the save derives `vendor_floor_indexing` from
  // that base. Holding it as its own state let the two disagree — a new adapter
  // showed "One-based" beside a base of 0 and was then stored zero_based.
  // Deriving it means the dropdown always shows what will be saved.
  // LIFT-1196 retired the dropdown for non-LN1 protocols — the server ignores
  // vendor_floor_indexing there, so the base stands on its own and the save
  // echoes the stored string back unchanged. A NEW adapter has no protocol
  // selected yet ("" is not a protocol), and keeping today's LN1-style
  // derivation for that blank form is what the new-adapter tests pin: a save
  // is blocked until a protocol is chosen, so the blank state never writes.
  const ln1FloorIndexing = protocolType === "" || isLn1FamilyProtocol(protocolType);
  const vendorFloorIndexing =
    positionFloorBase === 0 ? VendorFloorIndexing.ZERO_BASED : VendorFloorIndexing.ONE_BASED;
  const [adapterActive, setAdapterActive] = useState(true);
  // Opt-in for per-key Key.Direction. Off by default and off for every adapter
  // that predates the feature, which is what keeps the editor unchanged for
  // them: the per-row Direction control is not rendered, and offset-overlap
  // detection ignores the direction axis entirely.
  const [directionAwareKeys, setDirectionAwareKeys] = useState(false);

  // Allow-empty overrides for auth credentials
  const [allowEmptyUsername, setAllowEmptyUsername] = useState(false);
  const [allowEmptyPassword, setAllowEmptyPassword] = useState(false);

  // Edit mode
  const [editingKeyIds, setEditingKeyIds] = useState<string[]>([]);

  // Credentials carried over from a loaded adapter. When true, the server
  // already has credentials for this adapter and the user hasn't retyped
  // them into the form. We preserve them by (a) skipping the non-empty
  // username/password validation so unrelated edits (like intra-content)
  // can save, and (b) omitting auth_credentials from the PUT body so the
  // backend keeps whatever's already stored.
  const [credentialsPreserved, setCredentialsPreserved] = useState(false);

  // The loaded adapter has stored credentials whose password came back empty.
  // For anyone but a MASTER ADMIN that is the server's mask, not the password
  // (LIFT-1085): GET clears it, and an update carrying an empty password keeps
  // the stored one. See `passwordMasked` below.
  const [storedPasswordHidden, setStoredPasswordHidden] = useState(false);

  // Dirty tracking: true when user has made any change since load/save
  const [isDirty, setIsDirty] = useState(false);

  // Unresolved legacy offset entries — populated when deserializeOffsetMapping
  // returns entries that could not be matched to any matrix column.
  const [unresolvedLegacyEntries, setUnresolvedLegacyEntries] = useState<LegacyOffsetEntry[]>([]);

  // Controls the dismissable Alert for unresolved legacy entries.
  // Reset to false whenever the user switches to a different adapter.
  const [unresolvedAlertDismissed, setUnresolvedAlertDismissed] = useState(false);

  // Validation state
  const [versionTouched, setVersionTouched] = useState(false);

  // Save read-back verification state
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mceSaveFailure, setMceSaveFailure] = useState<MceSaveFailure | null>(null);
  /**
   * Bytes two different cars both claim, surfaced when the operator saves.
   *
   * The matrix already flags these in place, but an operator saving an adapter
   * is not necessarily looking at the matrix -- and the server reports the same
   * bytes on every CDO build afterwards, where nobody sees it at all. A
   * warning rather than a block: the mapping is the operator's to judge, and
   * refusing the save would leave them no way to store work in progress.
   */
  const [contestedByteWarning, setContestedByteWarning] = useState<string | null>(null);
  const [saveVerified, setSaveVerified] = useState<boolean | null>(null);
  // Tracks whether the offset matrix currently has unresolved overlaps.
  // Used by verifyAdapterSave to reject verification when overlaps exist.
  const [hasMatrixOverlaps, setHasMatrixOverlaps] = useState(false);
  // Whether the Overlap Analysis panel is showing over this form.
  const [overlapPanelOpen, setOverlapPanelOpen] = useState(false);

  // CDO sub-step state
  const [showCdoEditor, setShowCdoEditor] = useState(false);
  // Pre-edit snapshot captured at the end of loadAdapter, used by the
  // PUT save flow to compute which fields actually changed and forward
  // them through onSaveSuccess. See AdapterEditSnapshot above.
  const editSnapshotRef = useRef<AdapterEditSnapshot | null>(null);

  const [cdoState, setCdoState] = useState<CdoStateBundle>({
    ...INITIAL_CDO_STATE,
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostAdapter,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostAdapterMutation();

  const [triggerGetAdapter] = useLazyGetAdapterQuery();

  // Optional wizard context — null when AdapterEditor is rendered standalone
  // (edge-management page) rather than inside a WizardProvider. The
  // stale-server detection below no-ops in standalone mode because there's
  // no wizard banner to surface the error.
  const wizardCtx = useWizardOptional();

  // Watch the POST result. When the backend returns "No server with that id
  // exists", the wizard's serverId is stale — typically because edge-to-cloud
  // sync soft-deleted the server without cascading to its channels/adapters
  // (see sync/bidir/apply.go's raw UPDATE). Report it through the wizard so
  // the top-level banner surfaces a clear, actionable message and a
  // "Go to Server step" recovery action.
  useEffect(() => {
    if (!isErrorPost || !wizardCtx) return;
    const msg = detectStaleServerErrorMessage(errorPost);
    if (msg) {
      wizardCtx.reportStaleServerError(msg);
    }
  }, [isErrorPost, errorPost, wizardCtx]);

  const { protocolsLight } = useGetAllProtocolsLight({});

  const [triggerPutAdapter, { isLoading: isLoadingPut }] = usePutAdapterMutation();

  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string, variableName: string) => {
    setIsDirty(true);
    if (variableName === "name") setAdapterName(newValue);
    if (variableName === "version") {
      setVersion(newValue);
      setVersionTouched(true);
    }
    if (variableName === "username") {
      setUsername(newValue);
      setCredentialsPreserved(false);
    }
    if (variableName === "password") {
      setPassword(newValue);
      setCredentialsPreserved(false);
    }
  };

  const onChangeProtocol = (selectedName: string) => {
    setIsDirty(true);
    setProtocolType(selectedName);
    setShowCdoEditor(false);
    setCdoState({ ...INITIAL_CDO_STATE });
    if (!LEADING_ZEROES_PROTOCOLS.includes(selectedName.toUpperCase())) {
      setLeadingZeroesId("");
    }
  };

  const handleCdoChange = (value: React.SetStateAction<CdoStateBundle>) => {
    setIsDirty(true);
    setCdoState(value);
  };

  /**
   * Build the columns for the AdapterOffsetMatrix from the adapter's own
   * key mappings, using the same `extractLn1MappedTypes` function the bank
   * matrix uses. This yields grouped category columns for normal mappings
   * plus individual columns (composite keys like "service:normal:5") for
   * bank_level mappings. When `ln1KeyMappings` is empty (no CDO configured
   * yet) the AdapterOffsetMatrix falls back to its protocol-based default
   * column set.
   */
  const adapterMappedTypes = useMemo<MappedMessageType[]>(
    () =>
      computeAdapterMappedTypes(
        protocolType,
        cdoState.ln1KeyMappings,
        cdoState.ln1IntraContentFlags,
        cdoState.ln1UnitOffsets,
      ),
    [protocolType, cdoState.ln1KeyMappings, cdoState.ln1IntraContentFlags, cdoState.ln1UnitOffsets],
  );

  /**
   * Derive the set of MODBUS register-spaces (COIL/DI/HR/IR) the adapter
   * actually touches, based on cdoState.cdoModbus.reads / .writes.
   *
   * Each read row carries a `readType` (ModbusReadType enum) and each write
   * carries a `writeType` (ModbusWriteType enum) — those Function Code enums
   * are the source of truth for which register space the operation targets.
   * The result is a self-referential `{COIL: "COIL", HR: "HR", …}` map
   * because:
   *   - buildAdapterOffsetColumns only inspects `Object.values(...)` to
   *     decide which MODBUS columns to render (canonical-ordered);
   *   - AdapterOffsetMatrix uses `regTypes[col.key]` to skip overlap
   *     detection between columns belonging to different register spaces,
   *     and MODBUS column keys ARE register-type strings, so the self-map
   *     is correct (e.g. regTypes["COIL"] === "COIL").
   *
   * Returns {} for non-MODBUS protocols. Declared BEFORE adapterOffsetColumns
   * because the column derivation depends on the set of register types in use.
   */
  const modbusRegTypes = useMemo<Record<string, string>>(() => {
    if (protocolType.toUpperCase() !== "MODBUS") return {};
    const modbus = (cdoState as { cdoModbus?: unknown }).cdoModbus;
    if (!modbus || typeof modbus !== "object") return {};

    const out: Record<string, string> = {};
    const reads = (modbus as { reads?: Array<{ readType?: number }> }).reads ?? [];
    const writes = (modbus as { writes?: Array<{ writeType?: number }> }).writes ?? [];

    reads.forEach((r) => {
      if (typeof r.readType !== "number") return;
      const rt = MODBUS_READ_TYPE_TO_REG_TYPE[r.readType];
      if (rt) out[rt] = rt;
    });
    writes.forEach((w) => {
      if (typeof w.writeType !== "number") return;
      const rt = MODBUS_WRITE_TYPE_TO_REG_TYPE[w.writeType];
      if (rt) out[rt] = rt;
    });

    return out;
  }, [protocolType, cdoState]);

  const adapterOffsetColumns = useMemo<AdapterOffsetColumn[] | undefined>(
    () =>
      buildAdapterOffsetColumns(
        protocolType,
        adapterMappedTypes,
        cdoState.ln1UnitOffsets,
        cdoState.ln1IndividualOffsets,
        modbusRegTypes,
      ),
    [
      protocolType,
      adapterMappedTypes,
      cdoState.ln1UnitOffsets,
      cdoState.ln1IndividualOffsets,
      modbusRegTypes,
    ],
  );

  /**
   * Derive the AdapterOffsetMap from cdoState for the AdapterOffsetMatrix component.
   * For LN1-family: combine ln1UnitOffsets (MessageContent-keyed grouped
   * columns) with ln1IndividualOffsets (composite-keyed bank_level columns).
   * For MODBUS: convert modbusRegisterOffsets (split initial/delta) to unified map.
   */
  const adapterOffsetMap = useMemo<AdapterOffsetMap>(
    () =>
      buildAdapterOffsetMap(
        protocolType,
        adapterMappedTypes,
        adapterOffsetColumns,
        cdoState.ln1UnitOffsets,
        cdoState.ln1IndividualOffsets,
        cdoState.modbusRegisterOffsets,
      ),
    [
      protocolType,
      cdoState.ln1UnitOffsets,
      cdoState.ln1IndividualOffsets,
      cdoState.modbusRegisterOffsets,
      adapterOffsetColumns,
      adapterMappedTypes,
    ],
  );

  /**
   * How many cars the overlap check runs over — and the note that says so when
   * the two available answers disagree.
   *
   * `units` is not the site's car count. It is seeded by `deriveUnitCount` from
   * whatever unit rows the STORED offset mapping happens to carry: 1 for the
   * legacy `[initial, initial+delta]` pair shape, and 8 for anything the
   * serializer wrote, because `DEFAULT_OFFSET_UNITS` emits eight rows regardless
   * of how many lifts exist. Both are artefacts of storage. Trusting either
   * fails in both directions — at 1 or 2 the cross-car bleed this report was
   * written for is invisible AND unwarned, and at 8 the report names cars that
   * do not exist and counts their collisions in its headline.
   *
   * The bank being commissioned knows its own lifts, so when the form was given
   * a bank that count wins. Where they disagree the operator is told, rather
   * than the disagreement being silently resolved into a number nothing on the
   * screen would let them question.
   */
  const bankLiftCount = bankContext?.lifts?.length ?? 0;
  /**
   * How many cars this adapter would expand to with no cap in the way.
   *
   * Named once and shared, because the caption and the report used to derive it
   * separately — the caption from `units`, the report from this precedence — and
   * two derivations of "how many cars" on one screen disagree the moment a bank
   * is present and its lift count differs from the stored mapping's. The caption
   * would say "enumerates 8 cars" beside a report saying "2 cars checked", from
   * the same render.
   */
  const enumeratedCarCount = bankLiftCount > 0 ? bankLiftCount : units;
  const analysisCarCount = boundedAnalysisCarCount(bankLiftCount, units, maxCars, maxCarsOrigin);
  const carCountNote = useMemo(() => {
    const enumerated = enumeratedCarCount;
    const notes: string[] = [];

    if (bankLiftCount > 0 && bankLiftCount !== units) {
      notes.push(
        `This adapter's stored offset mapping describes ${units} car(s), but ` +
          `${bankContext?.bankName ?? "this bank"} has ${bankLiftCount}.`,
      );
    }

    // Said out loud because the cap SHRINKS what was checked. An operator who
    // caps at 5 and reads "no overlapping offsets" is entitled to know that the
    // sentence covers five cars, not the eight the adapter enumerates —
    // otherwise a clean report reads as proof the whole adapter is clean.
    if (maxCarsOrigin !== "auto" && maxCars !== null && maxCars > 0 && maxCars < enumerated) {
      notes.push(
        `Max Cars caps expansion at ${maxCars}, so cars ${maxCars + 1}–${enumerated} ` +
          `were not projected and no collision among them is reported.`,
      );
    }

    if (notes.length === 0) return null;

    notes.push(`The analysis used ${analysisCarCount} car(s).`);

    return notes.join(" ");
  }, [
    bankLiftCount,
    units,
    bankContext,
    enumeratedCarCount,
    maxCars,
    maxCarsOrigin,
    analysisCarCount,
  ]);

  /**
   * The overlap run behind BOTH the matrix's red cells and the Overlap Analysis
   * report.
   *
   * `computeAdapterOverlaps` is the same function the matrix calls, given the
   * same inputs, so the report cannot list a collision the matrix leaves
   * unpainted or miss one it paints. Recomputing here rather than reading a
   * value back out of the child is deliberate: the map's identity changes on
   * every render, and lifting it through a callback would re-enter this
   * component's state on every pass. The boolean the matrix does report
   * (`onOverlapChange`) is safe precisely because it is a boolean.
   */
  const overlapAnalysis = useMemo(
    () =>
      computeAdapterOverlaps({
        protocolType,
        value: adapterOffsetMap,
        columns: adapterOffsetColumns,
        adapterMappedTypes,
        ln1KeyMappings: cdoState.ln1KeyMappings,
        intraContentFlags: cdoState.ln1IntraContentFlags,
        numCars: analysisCarCount,
        floors,
        regTypes: modbusRegTypes,
      }),
    [
      protocolType,
      adapterOffsetMap,
      adapterOffsetColumns,
      adapterMappedTypes,
      cdoState.ln1KeyMappings,
      cdoState.ln1IntraContentFlags,
      analysisCarCount,
      floors,
      modbusRegTypes,
    ],
  );

  /**
   * The report, kept in step with the form rather than snapshotted.
   *
   * The count on the button comes from here too, so the number the operator is
   * offered before opening the report is the number of rows they then see.
   * Deriving it from the detection map's size instead would have shown 22 for a
   * report of 12 — the map files every collision under both of its cells.
   */
  const overlapReport = useMemo(
    () =>
      buildOverlapReport(overlapAnalysis.overlaps, {
        carsAnalyzed: overlapAnalysis.numCars,
        columnsChecked: overlapAnalysis.columnSpecs.length,
        scopeNote: carCountNote,
      }),
    [overlapAnalysis, carCountNote],
  );

  /**
   * Open the report where the operator asked for it.
   *
   * A blocked pop-up falls back to the panel for THIS click and says so; it
   * does not rewrite the stored preference, because silently changing a
   * setting on the strength of one blocked click is worse than the block.
   *
   * The stamp carries the DATE as well as the time. The whole justification for
   * the separate window being a frozen copy is that stamp, and a bare
   * "1:11:17 PM" makes a report printed yesterday and carried to the machine
   * room typographically identical to one taken a minute ago.
   */
  const handleOpenOverlapAnalysis = useCallback(() => {
    if (overlapOpenIn === "window") {
      const html = renderOverlapReportHtml(overlapReport, {
        adapterName: adapterName || "Unnamed adapter",
        protocolType,
        capturedAt: new Date().toLocaleString(),
      });
      if (openOverlapAnalysisWindow(html) !== null) {
        return;
      }
      antdMessage.warning("Pop-up blocked, showing the overlap analysis in this window instead.");
    }
    setOverlapPanelOpen(true);
  }, [overlapOpenIn, overlapReport, adapterName, protocolType]);

  /**
   * Project the largest car count these offsets can serve before per-car
   * expansion collides with itself.
   *
   * Runs HERE, in the browser, because this is the only place the authored
   * initial/delta pair exists at all. The server would have to reverse-engineer a
   * delta from a table that may already collide, and a delta inferred from bad
   * data is not a bound worth writing. The server's job is enforcement.
   *
   * The sources it reads, and why there are two, are documented on
   * projectAdapterMaxCars above.
   */
  const maxCarsProjection = useMemo<MaxCarsProjection>(
    () =>
      projectAdapterMaxCars({
        columns: adapterOffsetColumns ?? [],
        mappedTypes: adapterMappedTypes,
        keyMappings: cdoState.ln1KeyMappings,
        floors,
        regTypes: modbusRegTypes,
        offsetMap: adapterOffsetMap,
        storedMapping: storedOffsetMapping,
      }),
    [
      adapterOffsetColumns,
      adapterMappedTypes,
      cdoState.ln1KeyMappings,
      adapterOffsetMap,
      storedOffsetMapping,
      floors,
      modbusRegTypes,
    ],
  );

  /**
   * Auto-fill the cap — and ONLY into a box nobody has expressed an opinion
   * about.
   *
   * The permission lives in shouldAutoFillMaxCars, on the PROVENANCE rather than
   * on the value, which is what makes "auto never clobbers a human" structural:
   * a stored cap, a cap this already filled, a number just typed and a box just
   * cleared are four different states and only the fourth-from-none is writable.
   * It is idempotent too — the fill sets the origin to "auto", so a projection
   * re-run by an unrelated edit leaves the value alone.
   *
   * It also refuses to write a cap below MIN_AUTO_FILL_CARS. A projection of one
   * car is a defect report, not a cap, and writing it from a background effect
   * would demolish a multi-car site. The caption says so instead.
   */
  useEffect(() => {
    if (!shouldAutoFillMaxCars(maxCarsOrigin, maxCarsProjection)) return;

    setMaxCars(maxCarsProjection.safeCarCount);
    setMaxCarsOrigin("auto");
  }, [maxCarsOrigin, maxCarsProjection]);

  /**
   * Clamp the car-count control to the cap, and SAY SO when it bites.
   *
   * Silent clamping here would be the same sin the cap exists to prevent one
   * layer down: a number quietly becoming a different number.
   */
  const handleUnitsChange = useCallback(
    (next: number) => {
      if (maxCars !== null && next > maxCars) {
        setUnits(maxCars);
        const why = maxCarsProjection.bound
          ? ` — expansion is bounded at ${describeMaxCarsBound(maxCarsProjection.bound)}`
          : "";
        antdMessage.warning(
          `Capped at ${maxCars} car${maxCars === 1 ? "" : "s"} by this adapter's Max Cars${why}`,
        );

        return;
      }

      setUnits(next);
    },
    [maxCars, maxCarsProjection],
  );

  /**
   * Handle changes from AdapterOffsetMatrix: translate the map back to cdoState.
   * For LN1-family: update ln1UnitOffsets with [initial, initial+delta] per category.
   * For merged (intra-content) columns, write the same pair to EVERY member so
   * the participating MessageContent arrays stay in sync — otherwise only the
   * alphabetically-first member would be updated and the others would persist
   * stale values in the generated Ln1.Mapping.
   * For MODBUS: update modbusRegisterOffsets directly.
   */
  const handleAdapterOffsetChange = useCallback(
    (next: AdapterOffsetMap) => {
      setIsDirty(true);
      if (isLn1FamilyProtocol(protocolType)) {
        setCdoState((prev) => {
          const newGrouped = { ...prev.ln1UnitOffsets };
          const newIndividual = { ...(prev.ln1IndividualOffsets ?? {}) };
          Object.entries(next).forEach(([colKey, { initial, delta }]) => {
            if (colKey.includes(":")) {
              // Composite key (bank_level individual message) — e.g. "service:normal:5"
              newIndividual[colKey] = [initial, initial + delta];
              return;
            }
            // Plain category key — e.g. "service". Fan out to all member
            // categories for merged columns so every MessageContent gets the
            // same [initial, initial+delta] pair.
            const mt = adapterMappedTypes.find((m) => m.categoryKey === colKey);
            const memberKeys = mt && mt.categoryKeys.length > 1 ? mt.categoryKeys : [colKey];
            memberKeys.forEach((ck) => {
              const mc = categoryKeyToMessageContent(ck);
              if (mc !== MessageContent.NONE) {
                newGrouped[mc] = [initial, initial + delta];
              }
            });
          });
          return {
            ...prev,
            ln1UnitOffsets: newGrouped,
            ln1IndividualOffsets: newIndividual,
          };
        });
      } else if (protocolType.toUpperCase() === "MODBUS") {
        setCdoState((prev) => {
          const newInitial: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>> = {};
          const newDelta: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>> = {};
          (["COIL", "DI", "HR", "IR"] as const).forEach((k) => {
            if (next[k]) {
              newInitial[k] = next[k].initial;
              newDelta[k] = next[k].delta;
            }
          });
          return {
            ...prev,
            modbusRegisterOffsets: { initial: newInitial, delta: newDelta },
          };
        });
      }
    },
    [protocolType, adapterMappedTypes],
  );

  /**
   * Build the secondaryByPairId lookup so buildKeyRequestBody can fold a
   * paired secondary row into the primary's lower_* fields.
   */
  const buildSecondaryByPairId = (mappings: Ln1KeyMappingRow[]): Map<string, Ln1KeyMappingRow> => {
    const map = new Map<string, Ln1KeyMappingRow>();
    mappings.forEach((row) => {
      if (row.is_pair_secondary && row.pair_id) {
        map.set(row.pair_id, row);
      }
    });
    return map;
  };

  /**
   * Dispatch a BulkKeyRequest to POST /keys/bulk and thread the server-assigned
   * UUIDs for newly created keys back into cdoState.ln1KeyMappings.
   *
   * This is the single entry point for all key persistence — both the initial
   * create-new-adapter path (all rows are creates, no prior ids) and the
   * edit-existing-adapter path (mix of create / update / delete derived from
   * the diff against oldKeyIds).
   *
   * One HTTP request per save regardless of how many keys changed.
   */
  const dispatchBulkKeys = async (
    adapterId: string,
    oldKeyIds: string[],
    mappings: Ln1KeyMappingRow[],
    deviceType: string,
    token: string,
  ): Promise<void> => {
    const secondaryByPairId = buildSecondaryByPairId(mappings);
    const primaryMappings = mappings.filter((row) => !row.is_pair_secondary);

    const toUpdate = primaryMappings.filter((row) => row.id);
    const toCreate = primaryMappings.filter((row) => !row.id);
    const currentIds = new Set(toUpdate.map((row) => row.id as string));
    const toDelete = oldKeyIds.filter((id) => id && !currentIds.has(id));

    if (toCreate.length === 0 && toUpdate.length === 0 && toDelete.length === 0) {
      return;
    }

    const createKeyTs: KeyT[] = toCreate
      .map((row) => {
        const secondary = row.pair_id != null ? secondaryByPairId.get(row.pair_id) : undefined;
        return ln1RowToKeyT(row, adapterId, deviceType, secondary);
      })
      .filter((k): k is KeyT => k !== null);

    const updateKeyTs: KeyT[] = toUpdate
      .map((row) => {
        const secondary = row.pair_id != null ? secondaryByPairId.get(row.pair_id) : undefined;
        const keyT = ln1RowToKeyT(row, adapterId, deviceType, secondary);
        if (!keyT) return null;
        keyT.id = stringToUuidT(row.id as string);
        return keyT;
      })
      .filter((k): k is KeyT => k !== null);

    const deleteUuidTs: UuidT[] = toDelete.map((id) => stringToUuidT(id));

    const reqT = new BulkKeyRequestT(
      stringToUuidT(adapterId),
      createKeyTs,
      updateKeyTs,
      deleteUuidTs,
    );
    const bytes = packToBytes(reqT);

    const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
    const resp = await sendFlatBufferBinary(`${apiBase}/keys/bulk`, bytes, token, "POST");

    if (!resp.ok) {
      const msg = await resp.text();
      throw new Error(`/keys/bulk failed (${resp.status}): ${msg}`);
    }

    const respBuf = new Uint8Array(await resp.arrayBuffer());
    const fbBuf = new flatbuffers.ByteBuffer(respBuf);
    const respT = BulkKeyResponse.getRootAsBulkKeyResponse(fbBuf).unpack();

    if (respT.errors.length > 0) {
      throw new Error(`/keys/bulk returned errors: ${respT.errors.join("; ")}`);
    }

    if (respT.createdIds.length === toCreate.length && toCreate.length > 0) {
      setCdoState((prev) => {
        const nextMappings = prev.ln1KeyMappings.slice();
        toCreate.forEach((row, i) => {
          const rowIdx = nextMappings.indexOf(row);
          if (rowIdx >= 0) {
            const assignedId = formatUuidT(respT.createdIds[i]);
            nextMappings[rowIdx] = { ...row, id: assignedId };
          }
        });
        return { ...prev, ln1KeyMappings: nextMappings };
      });
    }
  };

  /**
   * POST all key mappings via the bulk endpoint. Used for initial adapter
   * creation (no prior key IDs to diff against).
   */
  const postKeysForAdapter = async (
    adapterId: string,
    mappings: Ln1KeyMappingRow[],
    deviceType: string,
    token: string,
  ): Promise<void> => {
    await dispatchBulkKeys(adapterId, [], mappings, deviceType, token);
  };

  /**
   * Diff-based sync via the bulk endpoint: compute {create, update, delete}
   * sets from the previously-loaded key IDs (`oldKeyIds`) vs. the current
   * mappings, then send a single POST /keys/bulk.
   */
  const syncKeysForAdapter = async (
    adapterId: string,
    oldKeyIds: string[],
    mappings: Ln1KeyMappingRow[],
    deviceType: string,
    token: string,
  ): Promise<void> => {
    await dispatchBulkKeys(adapterId, oldKeyIds, mappings, deviceType, token);
  };

  const clearForm = () => {
    setAdapterName("");
    setProtocolType("");
    setLiftType("ELEVATOR");
    setVersion("1.0.0");
    setAuthType("NONE");
    setUsername("");
    setPassword("");
    setFloors(1);
    setUnits(1);
    // The cap resets WITH its provenance, and that matters more than for the
    // fields around it: a cap now BOUNDS the overlap analysis, so a number left
    // behind from the previously-opened adapter would silently narrow a brand-new
    // one's analysis — and a "stored" provenance would make that leaked number
    // outrank the auto-detection that should have run on the new adapter.
    setMaxCars(null);
    setMaxCarsOrigin("unset");
    setLeadingZeroesId("");
    setAdapterActive(true);
    setVersionTouched(false);
    setAllowEmptyUsername(false);
    setAllowEmptyPassword(false);
    setCredentialsPreserved(false);
    setStoredPasswordHidden(false);
    // Nothing will be loaded for the next adapter, so the saved indexing
    // string goes with it — a new non-LN1 adapter saves zero_based.
    setStoredVendorFloorIndexing(null);
    setShowCdoEditor(false);
    setCdoState({ ...INITIAL_CDO_STATE });
    setIsDirty(false);
  };

  const isBasicFieldsValid = () =>
    adapterName.trim() !== "" &&
    protocolType.trim() !== "" &&
    version.trim() !== "" &&
    isValidSemver(version);

  const cdoValid = isCdoSaveValid(protocolType, cdoState, formAction);
  const needsCredentials = authType === "PASSWORD" || authType === "APIKEY";
  // The stored password was withheld from this caller. Leaving the field empty
  // keeps it, so an empty password is valid here and is shown as "unchanged"
  // rather than as a missing value to be waved through with "Allow empty".
  const passwordMasked = storedPasswordHidden && !callerSeesAdapterSecrets;
  const credentialsValid =
    !needsCredentials ||
    credentialsPreserved ||
    ((username.trim() !== "" || allowEmptyUsername) &&
      (password.trim() !== "" || allowEmptyPassword || passwordMasked));
  const isFullyValid = isBasicFieldsValid() && cdoValid && credentialsValid;

  const getAddButtonTooltip = (): string => {
    if (!isBasicFieldsValid()) {
      const missing: string[] = [];
      if (!adapterName.trim()) missing.push("adapter name");
      if (!protocolType.trim()) missing.push("protocol type");
      if (!version.trim() || !isValidSemver(version)) missing.push("valid version (semver)");
      return `Missing: ${missing.join(", ")}`;
    }
    if (!cdoValid) {
      if (!showCdoEditor) {
        return "Click 'Configure Mappings' to set up protocol-specific configuration";
      }
      return getCdoMissingMessage(protocolType, cdoState);
    }
    if (!credentialsValid) {
      return "Username and password are required (or check 'Allow empty' to override)";
    }
    return "";
  };

  const buildAdapterBody = (): Record<string, unknown> => {
    const isMce = protocolType.toUpperCase() === MCE_BINARY_CDO_CONFIG.protocolKey;
    const matchedProtocol = protocolsLight.find(
      (p: { name: string; defaultInterface: string }) =>
        p.name.toUpperCase() === protocolType.toUpperCase(),
    );

    let parsedInterface = {};
    if (matchedProtocol && matchedProtocol.defaultInterface) {
      try {
        parsedInterface =
          typeof matchedProtocol.defaultInterface === "string"
            ? JSON.parse(matchedProtocol.defaultInterface)
            : matchedProtocol.defaultInterface;
      } catch {
        parsedInterface = {};
      }
    }

    // The wire column behind this is a `varchar(10)` bound
    // `oneof=zero_based one_based` (LiftCloud's models/adapter.model.go),
    // NOT the FlatBuffer VendorFloorIndexing integer this editor keeps in
    // state: sending the raw enum number fails Go's *string unmarshal and
    // gin drops the field silently — the adapter saves, but floor indexing
    // reverts to whatever it already was on next load. KEY_FLOOR_INDEXING
    // carries the same two wire strings for Key.floor_indexing, so it's
    // reused here rather than a second lookup table that could drift out of
    // sync.
    // LN1 family (plus a new adapter whose protocol is not chosen yet): the
    // dropdown and Position Floor Base are one quantity, so the wire spelling
    // is derived from the position base. The server falls back to this enum
    // ONLY when position_floor_base is 0, so writing "one_based" beside a 0
    // would resurrect the ambiguity the pairing removes.
    // LIFT-1196 retired the column for every other protocol — the server now
    // ignores vendor_floor_indexing there, and position_floor_base /
    // call_floor_base take effect on their own — so a non-LN1 save echoes
    // back the string the adapter loaded with, unchanged. A new adapter
    // loaded nothing, so it sends zero_based: the only value a fresh
    // adapter can claim, and one the server ignores on that protocol.
    let vendorFloorIndexingPayload = storedVendorFloorIndexing ?? KEY_FLOOR_INDEXING.ZERO_BASED;
    if (ln1FloorIndexing) {
      vendorFloorIndexingPayload =
        positionFloorBase === 0 ? KEY_FLOOR_INDEXING.ZERO_BASED : KEY_FLOOR_INDEXING.ONE_BASED;
    }

    const body: Record<string, unknown> = {
      name: adapterName,
      protocol_type: protocolType,
      type: "WEBSOCKET",
      lift_type: liftType,
      version,
      auth_type: authType === "NONE" ? null : authType,
      // Only include auth_credentials when the user actually set/changed them.
      // Omitting preserves whatever the backend already has in auth_binary.
      ...(credentialsPreserved ? {} : { auth_credentials: { username, password } }),
      active: adapterActive,
      floors,
      // The strings above, not a second lookup: see the derivation.
      vendor_floor_indexing: vendorFloorIndexingPayload,
      // Plain integers: LiftCloud binds these as *int16, so a JSON number is
      // what the pointer expects. Unlike vendor_floor_indexing above there is
      // no string spelling to translate to.
      position_floor_base: positionFloorBase,
      call_floor_base: callFloorBase,
      direction_aware_keys: directionAwareKeys,
      // Nullable on the wire: absent means "no cap". Emphatically not `?? 0` —
      // the schema field is an optional int16, and 0 would be a cap of zero
      // cars, which the server floors to one.
      max_cars: maxCars,
      dictionary: {},
      interface: parsedInterface,
      // MCE's cdo_input is never built here: see requestSerializers. The one write of an
      // MCE mapping from this editor, a legacy event mapping edit, is added by handleUpdate.
      ...(isMce ? {} : { cdo_input: buildCdoPayload(protocolType, cdoState) }),
      total_bacnet_channels: 0,
      bacnet_config: cdoState.cdoBacnet,
      bacnet_bank_mappings: serializeMappings(cdoState.bacnetBankMappings) as any,
      socket_message_priority: {
        customConfiguration: true,
        rules: [],
        displayMapMode: "accumulate",
        iconStalenessTimeout: null,
      },
    };

    if (LEADING_ZEROES_PROTOCOLS.includes(protocolType.toUpperCase()) && leadingZeroesId) {
      body.leading_zeroes_id = leadingZeroesId;
    }

    // Modbus adapters: include cdo_template JSON alongside the binary
    // so that StepBanks can read it via adapter.cdo_template without a
    // separate binary fetch. Convert BigInt values to Number for JSON
    // serialization safety (pollingInterval is bigint). The matrix-level
    // Initial/Delta offsets live on cdoState.modbusRegisterOffsets (the
    // UI's nested {initial,delta} shape) and must be converted to the
    // canonical FlatBuffer `ModbusRegisterOffsetMapT` shape before going
    // on the wire — the backend's PackModbusTemplateFromJSON binds the
    // flat 8-field schema, and the UI's nested map cannot be unpacked
    // into ModbusTemplateT without normalization. The wire format we
    // emit here is the FB-class JSON (camelCase keys); LiftCloud's
    // normalizeModbusTemplateJSON rewrites those onto snake_case tags
    // before fbjson.Unmarshal binds them into ModbusTemplateT.
    if (protocolType.toUpperCase() === "MODBUS" && cdoState.cdoModbus) {
      const registerOffsetsFb = modbusRegisterOffsetsUiToFb(cdoState.modbusRegisterOffsets ?? null);
      const editorTemplateWithOffsets = {
        ...cdoState.cdoModbus,
        registerOffsets: registerOffsetsFb,
      };
      body.cdo_template = JSON.parse(
        JSON.stringify(editorTemplateWithOffsets, (_key, val) =>
          typeof val === "bigint" ? Number(val) : val,
        ),
      );
    }

    // OPC UA / LobbyVision2 adapters: unlike MODBUS (whose binary is sent
    // only via the separate PUT /adapters/:id/cdo endpoint, after create),
    // these MUST include cdo_binary directly in the CREATE body. Without
    // cdo_schema_name the backend's SchemaNameForProtocol lookup would
    // default the packer to "modbus_template" and mis-decode the binary (see
    // LiftCloud utils/flatbuffers/binary/packer.go schemaNames map). The
    // subsequent PUT in sendOpcUaCdoBinary/sendLobbyVision2CdoBinary (update
    // path / post-create refresh) re-sends the same binary through the
    // dedicated endpoint, mirroring the MODBUS flow. See
    // BinaryCdoProtocolRegistry for the shared config.
    if (protocolType.toUpperCase() === OPC_UA_BINARY_CDO_CONFIG.protocolKey) {
      body.cdo_binary = OPC_UA_BINARY_CDO_CONFIG.toBase64(
        OPC_UA_BINARY_CDO_CONFIG.build(cdoState.cdoOpcUa),
      );
      body.cdo_schema_name = OPC_UA_BINARY_CDO_CONFIG.schemaName;
    }

    if (protocolType.toUpperCase() === LOBBYVISION2_BINARY_CDO_CONFIG.protocolKey) {
      body.cdo_binary = LOBBYVISION2_BINARY_CDO_CONFIG.toBase64(
        LOBBYVISION2_BINARY_CDO_CONFIG.build(cdoState.cdoLobbyVision2),
      );
      body.cdo_schema_name = LOBBYVISION2_BINARY_CDO_CONFIG.schemaName;
    }

    // MCE's exclusion has its own field. A legacy row's is read-only, so it is
    // never sent; absent leaves what the server holds.
    if (isMce && cdoState.oneHotExclusion && isExclusionEditable(protocolType, cdoState)) {
      body.one_hot_exclusion_binary = oneHotExclusionToBase64(cdoState.oneHotExclusion);
    }

    Object.assign(body, buildOffsetFields(cdoState));

    return body;
  };

  /**
   * Read the adapter back from the API and verify key fields were saved correctly.
   * Uses partial-safe checks — only verifies fields that are relevant to the
   * current protocol, not a full form comparison. Handles incremental saves.
   */
  const verifyAdapterSave = async (
    adapterId: string,
    token: string,
  ): Promise<{ valid: boolean; message: string | null; cdoInputJson: string | null }> => {
    try {
      // fetchAdapterDirect bypasses RTK Query's cache entirely, guaranteeing a
      // fresh GET. The lazy trigger (triggerGetAdapter) can return stale cached
      // data from before the PUT even with forceRefetch:true, causing false
      // "name not saved" failures. This direct fetch is the reliable path.
      const data = await fetchAdapterDirect(adapterId, token);
      if (!data)
        return {
          valid: false,
          message: "Server returned no data for read-back",
          cdoInputJson: null,
        };

      // Name check
      const readName: string = (data.name as string) || "";
      if (readName.trim() !== adapterName.trim()) {
        return {
          valid: false,
          message: `Name not saved correctly: expected "${adapterName}", got "${
            readName || "(empty)"
          }"`,
          cdoInputJson: null,
        };
      }

      // CDO checks — protocol-specific presence check only, not full content equality
      const proto = protocolType.toUpperCase();
      // FlatBuffer responses use camelCase fields (cdoInputJson); JSON fallback uses snake_case (cdo_input).
      // Detect by whether protocolType came back as a number (FlatBuffer enum) or string/undefined (JSON).
      const isFbResponse = typeof (data as Record<string, unknown>).protocolType === "number";
      const cdoJson: string | null = (() => {
        if (isFbResponse)
          return ((data as Record<string, unknown>).cdoInputJson as string | null) ?? null;
        const raw =
          (data as Record<string, unknown>).cdo_input ??
          (data as Record<string, unknown>).cdoInput ??
          null;
        if (raw == null) return null;
        return typeof raw === "string" ? raw : JSON.stringify(raw);
      })();

      if (proto === MCE_BINARY_CDO_CONFIG.protocolKey) {
        // Only a row that had a binary can have lost it. A row that came back
        // as JSON, or with no CDO, is one this save did not touch.
        if (cdoState.cdoMce.stored?.binaryBase64) {
          const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
          const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
          if (!bytes || bytes.length === 0) {
            return {
              valid: false,
              message: "MCE CDO was not saved (cdo_binary is empty)",
              cdoInputJson: null,
            };
          }
          if (!MCE_BINARY_CDO_CONFIG.decode(bytes)) {
            return {
              valid: false,
              message: "Saved MCE CDO is not an MCEM mapping",
              cdoInputJson: null,
            };
          }
        }
      } else if (["LN1", "RVU", "CGA", "LOBBYVISION"].includes(proto)) {
        if (cdoState.cdoLn1.allowed_messages != null && !cdoJson) {
          return { valid: false, message: `${proto} CDO data was not saved`, cdoInputJson: null };
        }
      } else if (proto === "SEQUENT") {
        // Sequent readback: verify every discrete_2_message mapping the user
        // entered round-tripped through the server. LiftCloud has historically
        // returned 200 with an empty cdoBinary/cdoInputJson payload when its
        // Sequent CDO encoder drops discrete_2_message silently; surfacing the
        // mismatch here stops a broken save from proceeding to the publish
        // step where the less-specific "Add at least one discrete-to-message
        // mapping" validator fires.
        const sentCount = cdoState.cdoSequent.discrete_2_message.length;
        if (sentCount > 0) {
          let savedCount = 0;
          if (cdoJson && cdoJson !== "{}") {
            try {
              const parsed = JSON.parse(cdoJson);
              if (Array.isArray(parsed?.discrete_2_message)) {
                savedCount = parsed.discrete_2_message.length;
              }
            } catch {
              return {
                valid: false,
                message: "Sequent CDO data could not be parsed after save",
                cdoInputJson: cdoJson,
              };
            }
          }
          if (savedCount !== sentCount) {
            return {
              valid: false,
              message: `Sequent CDO was not saved: sent ${sentCount} discrete_2_message entries, server returned ${savedCount}. Likely a LiftCloud Sequent encoder bug — local state preserved; retry, or file a backend issue.`,
              cdoInputJson: cdoJson,
            };
          }
        }
      }
      // Modbus and OPC UA CDO binaries are sent via a separate endpoint — skip CDO verification here

      // Exclusion groups readback: verify they were saved when present
      if (proto === MCE_BINARY_CDO_CONFIG.protocolKey) {
        const sent = cdoState.oneHotExclusion?.groups.length ?? 0;
        const saved = decodeOneHotExclusion(
          (data as Record<string, unknown>).oneHotExclusionBinary as number[] | null | undefined,
        );
        if (sent > 0 && saved?.groups.length !== sent) {
          return {
            valid: false,
            message: `Exclusion group count mismatch: sent ${sent}, got ${
              saved?.groups.length ?? 0
            }`,
            cdoInputJson: null,
          };
        }
      } else if (cdoState.oneHotExclusion && cdoState.oneHotExclusion.groups.length > 0) {
        if (!cdoJson) {
          return {
            valid: false,
            message: "One-hot exclusion groups were not saved (CDO is empty)",
            cdoInputJson: null,
          };
        }
        try {
          const parsed = JSON.parse(cdoJson);
          const savedExclusion = parsed.one_hot_exclusion;
          if (!savedExclusion || !Array.isArray(savedExclusion.groups)) {
            return {
              valid: false,
              message: "One-hot exclusion groups missing from saved CDO data",
              cdoInputJson: cdoJson,
            };
          }
          if (savedExclusion.groups.length !== cdoState.oneHotExclusion.groups.length) {
            return {
              valid: false,
              message: `Exclusion group count mismatch: sent ${cdoState.oneHotExclusion.groups.length}, got ${savedExclusion.groups.length}`,
              cdoInputJson: cdoJson,
            };
          }
        } catch {
          return {
            valid: false,
            message: "Could not parse CDO data for exclusion verification",
            cdoInputJson: cdoJson,
          };
        }
      }

      // Overlap check: if the offset matrix has unresolved overlaps,
      // verification fails even though the server accepted the save.
      if (hasMatrixOverlaps) {
        return {
          valid: false,
          message:
            "Adapter saved, but offset matrix has overlapping registers — adjust offsets to resolve.",
          cdoInputJson: cdoJson,
        };
      }

      return { valid: true, message: null, cdoInputJson: cdoJson };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "unknown error";
      return { valid: false, message: `Verification GET failed: ${msg}`, cdoInputJson: null };
    }
  };

  const showMceSaveFailure = (failure: MceSaveFailure) => {
    setMceSaveFailure(failure);
    setSaveError(failure.message);
  };

  /**
   * Why an edited event mapping must not be saved, or null when it may. Only text that
   * changed is checked: a bad file already on the server must not block a change to
   * the path beside it.
   */
  const refuseEditedEventMapping = (edit: MceEventEdit | null): Promise<MceSaveFailure | null> => {
    const savedData = cdoState.cdoMce.stored?.savedEvent?.mapping_data ?? "";
    if (!edit || edit.event.mapping_data === savedData) return Promise.resolve(null);
    return refuseEventMapping(edit.event.mapping_data);
  };

  /**
   * Read the adapter's mapping back after an event mapping save, and say whether the
   * server kept what was sent. LiftCloud can answer 200 and keep the old bytes.
   */
  const confirmEventEdit = async (edit: MceEventEdit, adapterId: string, token: string) => {
    const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
    const base64 = await readStoredMapping(`${apiBase}/adapters/${adapterId}/cdo`, token);
    const stored = base64 ? MCE_BINARY_CDO_CONFIG.decode(base64ToBytes(base64)) : null;
    return {
      stored,
      mce: mceAfterEventSave(cdoState.cdoMce, stored),
      failure: eventEditReadBack(edit.event, stored, adapterId),
    };
  };

  const handleCreate = async () => {
    if (!isFullyValid) return;
    const token = await GETJwtToken();
    const body = buildAdapterBody();
    // An MCE mapping goes to /cdo once the adapter exists. Checking it first means a
    // bad upload leaves no adapter behind.
    const storedMce = cdoState.cdoMce.stored;
    if (
      protocolType.toUpperCase() === MCE_BINARY_CDO_CONFIG.protocolKey &&
      storedMce?.binaryBase64
    ) {
      setMceSaveFailure(null);
      setSaveError(null);
      const refused = await checkMappingOnServer(
        process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "",
        token,
        storedMce.binaryBase64,
      );
      if (refused) {
        showMceSaveFailure(refused);
        return;
      }
    }
    triggerPostAdapter({ body, token });
  };

  /** Send Modbus CDO as FlatBuffer binary to PUT /adapters/:id/cdo. */
  const sendModbusCdoBinary = async (adapterId: string, token: string) => {
    if (protocolType.toUpperCase() !== "MODBUS") return;
    try {
      // Splice the matrix-level Initial/Delta map onto the editor template
      // BEFORE packing so the FlatBuffer binary stored in cdo_binary carries
      // the user's register-offset settings. Without this, packModbusTemplate
      // serializes cdoState.cdoModbus alone — its `registerOffsets` slot is
      // null because the matrix UI mutates the sibling cdoState.modbusRegisterOffsets
      // map instead — and the runtime expansion path loses the offsets every
      // save cycle.
      const templateForPack: EditorModbusTemplate = {
        ...cdoState.cdoModbus,
        registerOffsets: cdoState.modbusRegisterOffsets ?? null,
      };
      const bytes = packModbusTemplate(templateForPack);
      // eslint-disable-next-line no-console
      console.log(
        "[ModbusCDO] Packing template:",
        cdoState.cdoModbus.reads?.length,
        "reads,",
        cdoState.cdoModbus.writes?.length,
        "writes,",
        bytes.length,
        "bytes",
      );
      const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
      const url = `${apiBase}/adapters/${adapterId}/cdo`;
      const resp = await sendFlatBufferBinary(url, bytes, token, "PUT");
      // eslint-disable-next-line no-console
      console.log("[ModbusCDO] PUT response:", resp.status, resp.statusText);
      if (!resp.ok) {
        const text = await resp.text();
        // eslint-disable-next-line no-console
        console.error("[ModbusCDO] PUT failed:", text);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ModbusCDO] sendModbusCdoBinary error:", err);
    }
  };

  /**
   * Build a "send binary CDO via PUT /adapters/:id/cdo" function for a
   * binary-CDO protocol (OPC UA, LobbyVision2). Mirrors sendModbusCdoBinary
   * — also called after CREATE (in addition to the cdo_binary already
   * included in the create body by buildAdapterBody) so the update path and
   * any post-create refresh always re-derive the binary from the current
   * cdoState rather than relying solely on the one-shot create body.
   * `getEditor` is read lazily at call time so the closure sees the latest
   * cdoState slot.
   */
  const makeSendBinaryCdo =
    <T,>(config: BinaryCdoProtocolConfig<T>, getEditor: () => T) =>
    async (adapterId: string, token: string) => {
      if (protocolType.toUpperCase() !== config.protocolKey) return;
      try {
        const bytes = config.build(getEditor());
        const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
        const url = `${apiBase}/adapters/${adapterId}/cdo`;
        const resp = await sendFlatBufferBinary(url, bytes, token, "PUT");
        if (!resp.ok) {
          const text = await resp.text();
          logError(config.putFailedLog, text);
        }
      } catch (err) {
        logError(config.errorLog, err);
      }
    };

  const sendOpcUaCdoBinary = makeSendBinaryCdo(OPC_UA_BINARY_CDO_CONFIG, () => cdoState.cdoOpcUa);
  const sendLobbyVision2CdoBinary = makeSendBinaryCdo(
    LOBBYVISION2_BINARY_CDO_CONFIG,
    () => cdoState.cdoLobbyVision2,
  );

  const handleUpdate = async () => {
    if (!isFullyValid || !existingAdapterId) return;
    setSaveError(null);
    setMceSaveFailure(null);
    setSaveVerified(null);
    setContestedByteWarning(contestedByteMessage(overlapReport));
    const token = await GETJwtToken();

    // A legacy MCE adapter's event mapping is the one part of its CDO this editor
    // writes, and only when it changed: a rename or any other edit sends no cdo_input.
    const eventEdit =
      protocolType.toUpperCase() === MCE_BINARY_CDO_CONFIG.protocolKey
        ? legacyEventEdit(cdoState.cdoMce)
        : null;
    const unfit = await refuseEditedEventMapping(eventEdit);
    if (unfit) {
      showMceSaveFailure(unfit);
      return;
    }
    const body = { ...buildAdapterBody(), ...eventEditBody(eventEdit) };
    const ifMatch = eventEdit
      ? await mceIfMatch(cdoState.cdoMce.stored?.binaryBase64 ?? null)
      : undefined;
    // What the form holds once this save is over, for the snapshot taken below.
    let savedCdo = cdoState;

    try {
      await triggerPutAdapter({
        id: existingAdapterId,
        body,
        token,
        ...(ifMatch ? { ifMatch } : {}),
      }).unwrap();

      // The PUT succeeded, so what we just sent IS the stored mapping now.
      //
      // The Max Cars projection reads this snapshot alongside the live matrix
      // and keeps the TIGHTER bound of the two. Leaving it at the value loaded
      // when the form opened makes the caption go on citing collisions the
      // operator has already fixed AND saved — which is how "bounded at car 4"
      // came to sit beside a report whose own first collision was at car 7, and
      // why auto-detect could propose a cap neither the matrix nor the saved
      // table justified any more.
      //
      // Only refresh when the field was actually sent: buildOffsetFields omits
      // it unless the offsets were edited, and the server's "only update when
      // len > 0" guard means an omitted field left the stored table untouched.
      // Overwriting the snapshot with [] there would discard the per-variant
      // series the matrix has no column for — the ones this source exists for.
      if (Array.isArray(body.offset_mapping)) {
        setStoredOffsetMapping(body.offset_mapping as OffsetMappingEntry[]);
      }

      // Verify CDO fields immediately after the main PUT, before sendModbusCdoBinary.
      // The /adapters/:id/cdo endpoint clears cdo_input on the server, so a GET after
      // it would return cdoInputJson: null. Verifying here ensures we see the freshly
      // saved cdo_input before any binary CDO write can overwrite it.
      // The returned cdoInputJson is passed to the round-trip logger so only one GET
      // is needed (previously a fire-and-forget parallel GET raced with this one).
      const {
        valid,
        message: verifyMessage,
        cdoInputJson,
      } = await verifyAdapterSave(existingAdapterId, token);
      logCdoSaveRoundTrip(protocolType, body.cdo_input, cdoInputJson);

      await sendModbusCdoBinary(existingAdapterId, token);
      await sendOpcUaCdoBinary(existingAdapterId, token);
      await sendLobbyVision2CdoBinary(existingAdapterId, token);

      if (eventEdit) {
        const outcome = await confirmEventEdit(eventEdit, existingAdapterId, token);
        savedCdo = { ...cdoState, cdoMce: outcome.mce };
        setCdoState((prev) => ({
          ...prev,
          cdoMce: mceAfterEventSave(prev.cdoMce, outcome.stored),
        }));
        if (outcome.failure) {
          showMceSaveFailure(outcome.failure);
          // A 200 that kept nothing is not a save. A save that could only not be read back is.
          if (!outcome.failure.saved) return;
        }
      }

      const uploaded = cdoState.cdoMce.stored;
      if (uploaded?.replaces && uploaded.binaryBase64) {
        const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
        const url = `${apiBase}/adapters/${existingAdapterId}/cdo`;
        const refused = await saveUploadedMapping(
          url,
          token,
          uploaded.binaryBase64,
          uploaded.replaces.stored?.binaryBase64 ?? null,
        );
        if (refused) {
          showMceSaveFailure(refused);
          return;
        }
        // It is saved either way. Without the server's bytes the form keeps no copy to name
        // in a later precondition, and a change waits for the reload the message asks for.
        const stored = await readStoredMapping(url, token);
        if (!stored) {
          showMceSaveFailure({
            saved: true,
            message: READ_BACK_FAILED,
            detail: `read-back of ${existingAdapterId} after a saved upload failed`,
          });
        }
        setCdoState((prev) => ({
          ...prev,
          cdoMce: { ...prev.cdoMce, stored: { kind: "embedded", binaryBase64: stored } },
        }));
      }

      await syncKeysForAdapter(
        existingAdapterId,
        editingKeyIds,
        cdoState.ln1KeyMappings,
        liftType,
        token,
      );
      // Compute which fields the user actually changed since the editor
      // hydrated. The wizard parent uses this to invalidate only the
      // downstream steps that depend on those fields — see
      // AdapterEditSnapshot above and ADAPTER_FIELD_RULES in
      // utils/wizardInvalidation.ts. Falls back to [] when no snapshot
      // was taken (defensive — non-edit mode shouldn't reach here).
      const currentSnapshot = buildAdapterSnapshot({
        name: adapterName,
        protocolType,
        active: adapterActive,
        cdoState: savedCdo,
      });
      const changedFields = diffAdapterSnapshots(editSnapshotRef.current, currentSnapshot);
      // Refresh the snapshot so a follow-up save with no further edits
      // produces an empty changedFields list (the user already accepted
      // the current state).
      editSnapshotRef.current = currentSnapshot;
      if (valid) {
        setSaveVerified(true);
        setIsDirty(false);
        onSaveSuccess?.(
          existingAdapterId,
          adapterName,
          protocolType,
          true,
          changedFields,
          verifyMessage ?? "Valid",
        );
      } else {
        // Keep isDirty=true so the Save button stays enabled and the user can
        // retry without re-entering mappings. Do NOT clear the form — local
        // cdoState (which still holds the user's unsaved mappings) is the only
        // source of truth until a subsequent save succeeds.
        setSaveError(verifyMessage);
        setSaveVerified(false);
        onSaveSuccess?.(
          existingAdapterId,
          adapterName,
          protocolType,
          false,
          changedFields,
          verifyMessage ?? undefined,
        );
      }
      if (formAction !== "edit" && valid) clearForm();
    } catch (err: unknown) {
      if (eventEdit) {
        // An event mapping save that is refused is one plain message and a log.
        showMceSaveFailure(eventEditFailure(err));
      } else {
        setSaveError(formatAdapterApiError(err));
      }
      setSaveVerified(false);
      // Mirror the POST-side detection: when an Update returns "No server
      // with that id exists" the wizard's serverId is stale; bubble it to
      // the wizard-level banner if we have a context to report against.
      const staleMsg = detectStaleServerErrorMessage(err);
      if (staleMsg && wizardCtx) {
        wizardCtx.reportStaleServerError(staleMsg);
      }
    }
  };

  /** Re-run server verification without a PUT. Used by the "Verify" button
   *  shown in clean edit mode (no unsaved changes). Updates the save banner
   *  and notifies the parent via onSaveSuccess so the table status tag refreshes. */
  const handleVerify = async () => {
    if (!existingAdapterId) return;
    setSaveError(null);
    setSaveVerified(null);
    const token = await GETJwtToken();
    try {
      const { valid, message: verifyMessage } = await verifyAdapterSave(existingAdapterId, token);
      if (valid) {
        setSaveVerified(true);
        onSaveSuccess?.(existingAdapterId, adapterName, protocolType, true, [], "Valid");
      } else {
        setSaveError(verifyMessage);
        setSaveVerified(false);
        onSaveSuccess?.(
          existingAdapterId,
          adapterName,
          protocolType,
          false,
          [],
          verifyMessage ?? undefined,
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "unknown error";
      setSaveError(msg);
      setSaveVerified(false);
    }
  };

  /** Log a compact summary of what was sent in cdo_input vs what the server
   *  returned. Uses the already-fetched cdoInputJson from verifyAdapterSave to
   *  avoid a redundant GET that would race with the verification call.
   *
   *  Skips silently when no saved JSON is available — that path means the
   *  read-back fetch failed or the server returned no cdo_input, neither of
   *  which is a real "round-trip mismatch" worth surfacing as a warning. The
   *  per-save summary is dev-only via devLog so production consoles aren't
   *  flooded with one info line per save. The warn is reserved for cases
   *  where we have real server data showing real drift. */
  const logCdoSaveRoundTrip = (
    proto: string,
    sentCdoInput: unknown,
    savedCdoInputJson: string | null,
  ): void => {
    if (!sentCdoInput || !savedCdoInputJson || savedCdoInputJson === "{}") return;
    try {
      const sentSummary = summarizeCdoInput(proto, sentCdoInput);
      let savedSummary: string;
      try {
        savedSummary = summarizeCdoInput(proto, JSON.parse(savedCdoInputJson));
      } catch {
        savedSummary = "<unparseable>";
      }
      devLog(`[AdapterSave:${proto}] sent=${sentSummary} saved=${savedSummary}`);
      if (sentSummary !== savedSummary) {
        // eslint-disable-next-line no-console
        console.warn(
          `[AdapterSave:${proto}] cdo_input round-trip mismatch — server dropped fields during save`,
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[AdapterSave] round-trip log failed:", err);
    }
  };

  /** Compact one-line summary of cdo_input payload for the save-trace log. */
  const summarizeCdoInput = (proto: string, cdo: unknown): string => {
    if (!cdo || typeof cdo !== "object") return "<empty>";
    const rec = cdo as Record<string, unknown>;
    const upper = proto.toUpperCase();
    if (upper === "SEQUENT") {
      const arr = rec.discrete_2_message;
      const count = Array.isArray(arr) ? arr.length : 0;
      return `SEQUENT(discrete_2_message=${count})`;
    }
    if (upper === "LN1" || upper === "RVU" || upper === "CGA" || upper === "LOBBYVISION") {
      const keys = Array.isArray(rec.ln1_key_mappings) ? rec.ln1_key_mappings.length : 0;
      return `${upper}(ln1_key_mappings=${keys})`;
    }
    if (upper === "MCE") {
      const idl =
        typeof rec.idl_file === "string" && rec.idl_file.length > 0 ? "present" : "absent";
      return `MCE(idl_file=${idl})`;
    }
    const ks = Object.keys(rec).length;
    return `${upper}(${ks} keys)`;
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Load existing adapter data for edit/duplicate
  useEffect(() => {
    if (!existingAdapterId) return;
    if (formAction !== "edit" && formAction !== "duplicate") return;

    const loadAdapter = async () => {
      try {
        const token = await GETJwtToken();
        const result = await triggerGetAdapter({
          id: existingAdapterId,
          token,
        }).unwrap();
        const adapterData = result?.data || result;
        if (!adapterData) return;

        setAdapterName(
          formAction === "duplicate" ? `${adapterData.name} (Copy)` : adapterData.name || "",
        );

        // adapterData is AdapterT (FlatBuffer *T object, camelCase integer enum fields) when
        // the server returns application/flatbuffers, or a plain JSON object (snake_case string
        // fields) when the fallback path is taken. Detect by whether protocolType is numeric.
        const isFlatBufferT = typeof adapterData.protocolType === "number";

        // Protocol type
        const resolvedProtocol = isFlatBufferT
          ? (AdapterProtocol[adapterData.protocolType as AdapterProtocol] ?? "")
          : (adapterData.protocol_type || "").toString();
        setProtocolType(resolvedProtocol.toUpperCase());

        // Lift type
        const resolvedLiftType = isFlatBufferT
          ? (LiftType[adapterData.liftType as LiftType] ?? "ELEVATOR")
          : (adapterData.lift_type || "ELEVATOR").toString();
        setLiftType(resolvedLiftType.toUpperCase());

        setVersion(adapterData.version || "1.0.0");

        // Auth type
        const resolvedAuthType = isFlatBufferT
          ? (AdapterAuth[adapterData.authType as AdapterAuth] ?? "NONE")
          : (adapterData.auth_type || "NONE").toString();
        setAuthType(resolvedAuthType.toUpperCase());

        // Decode auth credentials from both paths. If the server already has
        // credentials for this adapter (authBinary non-empty, or auth_credentials
        // object present), flag them as preserved so the user can make unrelated
        // edits without re-entering (and so we don't clobber them on save).
        if (isFlatBufferT) {
          const authBytes: number[] | undefined = adapterData.authBinary;
          if (authBytes && authBytes.length > 0) {
            setCredentialsPreserved(true);
            try {
              const buf = new flatbuffers.ByteBuffer(new Uint8Array(authBytes));
              const pc = PasswordContent.getRootAsPasswordContent(buf).unpack();
              const storedPassword = typeof pc.password === "string" ? pc.password : "";
              setUsername(typeof pc.username === "string" ? pc.username : "");
              setPassword(storedPassword);
              setStoredPasswordHidden(storedPassword === "");
            } catch (e) {
              const msg = e instanceof Error ? e.message : String(e);
              setSaveError(`Failed to decode auth credentials: ${msg}`);
            }
          }
        } else {
          const creds = adapterData.auth_credentials;
          if (creds && (creds.username || creds.password)) {
            setCredentialsPreserved(true);
            setStoredPasswordHidden(!creds.password);
          }
          setUsername(creds?.username || "");
          setPassword(creds?.password || "");
        }

        setFloors(adapterData.floors || 1);

        // Leading zeroes ID: AdapterT has UuidT, JSON has string
        const lzId = isFlatBufferT ? adapterData.leadingZeroesId : adapterData.leading_zeroes_id;
        setLeadingZeroesId(lzId && typeof lzId === "object" ? formatUuidT(lzId) : lzId || "");

        // Vendor floor indexing: AdapterT has the integer enum, JSON the
        // `one_based`/`zero_based` spelling. Both normalise to the enum member,
        // which is what the state, the Select and the payload all speak.
        const storedIndexing = toVendorFloorIndexing(
          isFlatBufferT ? adapterData.vendorFloorIndexing : adapterData.vendor_floor_indexing,
        );
        const storedPositionBase = Number(
          (isFlatBufferT ? adapterData.positionFloorBase : adapterData.position_floor_base) ?? 0,
        );

        // Absent (an adapter saved before the column existed, or a server that
        // omits it) is opted OUT — the same reading the server's
        // DirectionAwareKeysEnabled gives a nil column.
        setDirectionAwareKeys(
          (isFlatBufferT ? adapterData.directionAwareKeys : adapterData.direction_aware_keys) ===
            true,
        );

        // Show the EFFECTIVE position base, resolved the way the server
        // resolves it — never the raw column. A stored 0 is ambiguous: it means
        // both "unset, defer to Vendor Floor Indexing" and "explicitly
        // zero-based", and the server reads it as the former. So an adapter
        // carrying 0 beside a one-based dropdown actually numbers from 1, and
        // rendering the raw 0 would show a base the wire does not use. Saving
        // writes the pair back consistent (see the payload), so the ambiguity
        // survives only until an adapter is next edited.
        //
        // The derivation is LN1-only: LIFT-1196 keeps the server's
        // vendor_floor_indexing fallback for the LN1 family and IGNORES that
        // column for every other protocol (Modbus, Diligent, ...), where
        // position_floor_base now takes effect as stored. A non-LN1 adapter
        // therefore shows exactly what is stored — a stored 0 stays 0 — and
        // the save echoes the indexing string back unchanged.
        const effectivePositionBase =
          isLn1FamilyProtocol(resolvedProtocol) &&
          storedPositionBase === 0 &&
          storedIndexing === VendorFloorIndexing.ONE_BASED
            ? 1
            : storedPositionBase;

        setPositionFloorBase(effectivePositionBase);
        setCallFloorBase(
          Number((isFlatBufferT ? adapterData.callFloorBase : adapterData.call_floor_base) ?? 0),
        );
        // Keep the indexing the adapter arrived with, in its WIRE spelling,
        // so a non-LN1 save can echo it back unchanged. The FlatBuffer path
        // delivers the integer enum, so map its two members onto the varchar
        // strings; the JSON path delivers the spelling itself — absent (a
        // pre-column row) stays null and the save writes zero_based.
        let storedIndexingWire: string | null;
        if (isFlatBufferT) {
          storedIndexingWire =
            storedIndexing === VendorFloorIndexing.ONE_BASED
              ? KEY_FLOOR_INDEXING.ONE_BASED
              : KEY_FLOOR_INDEXING.ZERO_BASED;
        } else if (
          typeof adapterData.vendor_floor_indexing === "string" &&
          adapterData.vendor_floor_indexing !== ""
        ) {
          storedIndexingWire = adapterData.vendor_floor_indexing;
        } else {
          storedIndexingWire = null;
        }
        setStoredVendorFloorIndexing(storedIndexingWire);

        // The cap, decoded back out of its wire sentinel.
        //
        // `Archive.Adapter.max_cars` is `int16 = 0` because a FlatBuffer scalar
        // cannot carry null -- an absent slot and one written at its default are
        // the same bytes. The column behind it IS nullable and NULL is its "no
        // cap" value, so the schema spends 0 as the sentinel, and BOTH sides of
        // the wire have to honour it. requestSerializers encodes null as 0; this
        // is the half that decodes 0 back to null.
        //
        // Reading 0 literally is not cosmetic: 0 is a cap of ZERO cars, so the
        // field would render "0", the caption would announce that every car is
        // about to be dropped, and saving would persist it. That 0 is
        // meaningless as a real cap is precisely why it was safe to spend as the
        // sentinel -- a cap of ONE is the smallest genuine one, and stays
        // expressible.
        //
        // A stored value also disarms auto-fill for this load, which is what
        // makes an operator's number survive re-opening the editor over
        // colliding offsets.
        const storedMaxCars = isFlatBufferT ? adapterData.maxCars : adapterData.max_cars;
        const hydratedMaxCars =
          typeof storedMaxCars === "number" &&
          Number.isFinite(storedMaxCars) &&
          storedMaxCars !== MAX_CARS_WIRE_NO_CAP
            ? storedMaxCars
            : null;
        setMaxCars(hydratedMaxCars);
        // A stored cap disarms auto-detection; an adapter with none has still had
        // nothing expressed about it, so it stays writable. Collapsing these two
        // into one flag is what made a cleared box refill itself.
        setMaxCarsOrigin(hydratedMaxCars === null ? "unset" : "stored");

        setAdapterActive(adapterData.active ?? true);

        // Parse JSON string fields from AdapterT (stored as *Json suffixed strings).
        // JSON fallback has these as already-parsed objects under the plain field names.
        const parseFbJson = (s: string | Uint8Array | null | undefined): any => {
          if (!s) return null;
          try {
            return JSON.parse(s as string);
          } catch {
            return null;
          }
        };
        const cdoInputParsed = isFlatBufferT
          ? parseFbJson(adapterData.cdoInputJson)
          : adapterData.cdoInput;
        const bacnetConfigParsed = isFlatBufferT
          ? (parseFbJson(adapterData.bacnetConfigJson) ?? [])
          : (adapterData.bacnetConfig ?? []);
        const bacnetMappingsParsed = isFlatBufferT
          ? (parseFbJson(adapterData.bacnetBankMappingsJson) ?? [])
          : (adapterData.bacnetBankMappings ?? []);
        const offsetMapParsed = isFlatBufferT
          ? parseFbJson(adapterData.offsetMappingJson)
          : adapterData.offset_mapping;
        const intraFlagsParsed = isFlatBufferT
          ? parseFbJson(adapterData.intraContentFlagsJson)
          : adapterData.intra_content_flags;

        setUnits(deriveUnitCount(offsetMapParsed));

        // Keep the enumerated table itself, not just its car count: it is what
        // the cap is enforced against, and it carries per-variant series the
        // offset matrix has no column for.
        setStoredOffsetMapping(
          Array.isArray(offsetMapParsed) ? (offsetMapParsed as OffsetMappingEntry[]) : [],
        );

        // Build AdapterInterface-compatible shape for extractCdoState.
        // On the FlatBuffer path protocolType is already the numeric enum.
        // On the JSON fallback path protocolType is undefined (field is protocol_type);
        // derive the numeric enum from resolvedProtocol so extractCdoState works on both paths.
        const resolvedProtocolEnum: number = isFlatBufferT
          ? (adapterData.protocolType as number)
          : ((AdapterProtocol[
              resolvedProtocol.toUpperCase() as keyof typeof AdapterProtocol
            ] as number) ?? -1);

        // OPC UA / LobbyVision2 CDO is stored as FlatBuffer binary rather
        // than cdo_input JSON (union-heavy / FrontEndMessage-wrapping — the
        // same reason MODBUS fetches its binary via a separate endpoint
        // below). Fetch it here (BEFORE calling extractCdoState) so the
        // synchronous, pure extractCdoState function can decode it via its
        // binary branch. An adapter's protocol is single-valued (never both
        // OPCUA and LOBBYVISION2), so a single fetch/slot is sufficient —
        // see BinaryCdoProtocolRegistry.
        let cdoBinaryBytes: Uint8Array | null = null;
        if (
          getBinaryCdoProtocolByKey(
            AdapterProtocol[resolvedProtocolEnum as AdapterProtocol] ?? "",
          ) ||
          resolvedProtocolEnum === AdapterProtocol.MCE
        ) {
          const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
          cdoBinaryBytes = await fetchFlatBufferBinary(
            `${apiBase}/adapters/${existingAdapterId}/cdo`,
            token,
          );
        }

        const adapterForExtract: any = {
          protocolType: resolvedProtocolEnum,
          cdoInput: cdoInputParsed,
          cdoBinary: cdoBinaryBytes,
          bacnetConfig: Array.isArray(bacnetConfigParsed) ? bacnetConfigParsed : [],
          bacnetBankMappings: Array.isArray(bacnetMappingsParsed) ? bacnetMappingsParsed : [],
        };

        // Extract and populate CDO state
        const cdoExtracted = extractCdoState(adapterForExtract);
        const newCdo: CdoStateBundle = { ...INITIAL_CDO_STATE };
        if (cdoExtracted.cdoMce) newCdo.cdoMce = cdoExtracted.cdoMce;
        if (cdoExtracted.cdoLn1) newCdo.cdoLn1 = cdoExtracted.cdoLn1;
        if (cdoExtracted.cdoOpc) newCdo.cdoOpc = cdoExtracted.cdoOpc;
        if (cdoExtracted.cdoOpcUa) newCdo.cdoOpcUa = cdoExtracted.cdoOpcUa;
        if (cdoExtracted.cdoLobbyVision2) newCdo.cdoLobbyVision2 = cdoExtracted.cdoLobbyVision2;
        if (cdoExtracted.cdoSequent) newCdo.cdoSequent = cdoExtracted.cdoSequent;
        if (cdoExtracted.cdoBacnet) newCdo.cdoBacnet = cdoExtracted.cdoBacnet;
        if (cdoExtracted.bacnetBankMappings) {
          newCdo.bacnetBankMappings = deserializeMappings(cdoExtracted.bacnetBankMappings as any);
        }
        // Keys are not included in the AdapterT FlatBuffer schema, so
        // adapterData.keys is always undefined on the FlatBuffer path.
        // Use the embedded array when present (JSON fallback), otherwise
        // fetch keys separately from the dedicated endpoint.
        const embeddedKeys: any[] = adapterData.keys || [];
        let resolvedKeys: any[] = embeddedKeys;
        if (embeddedKeys.length === 0 && existingAdapterId) {
          try {
            const keysResult = await triggerGetAdapterKeys({
              adapterId: existingAdapterId,
              token,
            }).unwrap();
            resolvedKeys = keysResult?.data || [];
          } catch {
            // No keys for this adapter or keys endpoint unavailable — not an error for non-LN1 adapters
            resolvedKeys = [];
          }
        }
        if (resolvedKeys.length > 0) {
          newCdo.ln1KeyMappings = deserializeApiKeys(resolvedKeys);
        }
        // The server stores offset_mapping as []OffsetMappingEntry
        // ({message_type, message_value?, unit_number, offset}) — the
        // positional array format the Go backend unmarshals. Parse that
        // array into cdoState's keyed shape; fall back to the legacy object
        // paths for data written by older frontends.
        // Intra-content flags feed the column build below, so they must be in
        // newCdo before the descriptors are computed.
        if (
          intraFlagsParsed &&
          typeof intraFlagsParsed === "object" &&
          !Array.isArray(intraFlagsParsed)
        ) {
          newCdo.ln1IntraContentFlags = normalizeOffsetMap<boolean[]>(intraFlagsParsed);
        }
        // Column descriptors must come from the keys we just loaded into
        // newCdo, not from the `adapterMappedTypes` memo — that memo still
        // reflects the previously-selected adapter until the next render.
        // Without them every legacy friendly-name sub_type ("EnergizeCarCall",
        // "UpAssignedHallCall") fails to match a column, lands in
        // `unresolved`, and the Offset Matrix renders 0/0.
        const loadedColumns = toAdapterColumnDescriptors(
          computeAdapterMappedTypes(
            resolvedProtocol.toUpperCase(),
            newCdo.ln1KeyMappings,
            newCdo.ln1IntraContentFlags,
            offsetMapParsed,
          ),
        );
        const {
          ln1UnitOffsets: parsedGrouped,
          ln1IndividualOffsets: parsedIndividual,
          unresolvedLegacyEntries: parsedUnresolved,
        } = deserializeOffsetMapping(offsetMapParsed, loadedColumns);
        setUnresolvedLegacyEntries(parsedUnresolved);
        if (Object.keys(parsedGrouped).length > 0 || Object.keys(parsedIndividual).length > 0) {
          newCdo.ln1UnitOffsets = parsedGrouped;
          newCdo.ln1IndividualOffsets = parsedIndividual;
        } else {
          const migratedOffsetMap = migrateLegacyOffsetMap(offsetMapParsed);
          if (Object.keys(migratedOffsetMap).length > 0) {
            // Legacy object format — accept numeric-string and category-string keys.
            newCdo.ln1UnitOffsets = normalizeOffsetMap<number[]>(migratedOffsetMap);
          } else if (
            offsetMapParsed &&
            typeof offsetMapParsed === "object" &&
            !Array.isArray(offsetMapParsed)
          ) {
            // Oldest fallback — raw object of arrays.
            newCdo.ln1UnitOffsets = normalizeOffsetMap<number[]>(offsetMapParsed);
          }
        }
        // Modbus CDO: always use the FlatBuffer binary endpoint which returns
        // proper *T class instances with correctly typed union fields.
        // The cdo_template JSON from Go uses Type/Value union wrappers that
        // the TypeScript FlatBuffer Object API cannot parse.
        if (
          (isFlatBufferT && adapterData.protocolType === AdapterProtocol.MODBUS) ||
          (!isFlatBufferT && (adapterData.protocol_type || "").toUpperCase() === "MODBUS")
        ) {
          const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
          const cdoBytes = await fetchFlatBufferBinary(
            `${apiBase}/adapters/${existingAdapterId}/cdo`,
            token,
          );
          if (cdoBytes && cdoBytes.length > 0) {
            newCdo.cdoModbus = unpackModbusTemplate(cdoBytes);
            // ModbusTemplate now carries register_offsets natively in the
            // FlatBuffer binary, so unpackModbusTemplate already lifts it
            // back into the editor template's `registerOffsets` field.
            // Mirror that onto cdoState.modbusRegisterOffsets — the
            // matrix UI binds to the cdoState-level field, not to the
            // nested cdoModbus value.
            if (newCdo.cdoModbus.registerOffsets) {
              newCdo.modbusRegisterOffsets = newCdo.cdoModbus.registerOffsets;
            }
          }
          // Fall back to JSON-side parsing for legacy adapter rows that
          // pre-date the FB-schema field (or whose cdo_binary was
          // re-packed by a stale BeforeSave path that dropped the
          // offsets). Covers all three on-the-wire shapes.
          const cdoTemplateRaw = isFlatBufferT
            ? adapterData.cdoTemplateJson
            : adapterData.cdo_template;
          let cdoTemplateParsed: Record<string, unknown> = {};
          if (cdoTemplateRaw && typeof cdoTemplateRaw === "string") {
            cdoTemplateParsed = parseFbJson(cdoTemplateRaw) as Record<string, unknown>;
          } else if (cdoTemplateRaw && typeof cdoTemplateRaw === "object") {
            cdoTemplateParsed = cdoTemplateRaw as Record<string, unknown>;
          }
          if (!newCdo.modbusRegisterOffsets) {
            const fromJson = parseModbusRegisterOffsetsFromCdoTemplateJson(cdoTemplateParsed);
            if (fromJson) {
              newCdo.modbusRegisterOffsets = fromJson;
            }
          }
        }
        // Use cdoJsonFallback from extractCdoState when available
        if (cdoExtracted.cdoJsonFallback) {
          newCdo.cdoJsonFallback = cdoExtracted.cdoJsonFallback;
        }
        // Load one-hot exclusion groups from the CDO input.
        // Members may be composite key strings ("usage:normal:4") from the
        // current serialization format, or plain objects from legacy data.
        // Composite keys are reconstructed to MessageT via buildMessageTForEnum.
        // Empty/undecodable objects are filtered out (stale pre-schema data).
        if (resolvedProtocolEnum === AdapterProtocol.MCE) {
          // MCE reads its exclusion the way it writes it: the binary field.
          newCdo.oneHotExclusion = decodeOneHotExclusion(adapterData.oneHotExclusionBinary);
        } else if (cdoInputParsed?.one_hot_exclusion?.groups) {
          const raw = cdoInputParsed.one_hot_exclusion;
          newCdo.oneHotExclusion = new OneHotExclusionConfigT(
            raw.groups.map(
              (g: {
                category: number;
                categoryWide?: boolean;
                category_wide?: boolean;
                preservePositive?: boolean;
                preserve_positive?: boolean;
                evictOnlyWhenActive?: boolean;
                evict_only_when_active?: boolean;
                emitDeactivations?: boolean;
                emit_deactivations?: boolean;
                entries?: { members: any[] }[];
              }) => {
                const categoryWide = g.categoryWide ?? g.category_wide ?? false;
                // Preserve the stored group-level flags so a round-trip save
                // does not silently reset the user's non-default values
                // (LIFT-630). An absent flag falls back to the FlatBuffer
                // schema defaults (preserve_positive / evict_only_when_active
                // = true, emit_deactivations = false).
                const preservePositive = g.preservePositive ?? g.preserve_positive ?? true;
                const evictOnlyWhenActive =
                  g.evictOnlyWhenActive ?? g.evict_only_when_active ?? true;
                const emitDeactivations = g.emitDeactivations ?? g.emit_deactivations ?? false;
                const validEntries = (g.entries ?? [])
                  .map((e: { members: any[] }) => {
                    const validMembers = (e.members ?? [])
                      .map((m: unknown) => {
                        // Composite key string format (current): "usage:normal:4"
                        if (typeof m === "string") {
                          const parts = m.split(":");
                          if (parts.length >= 3) {
                            return buildMessageTForEnum(parts[0], parseInt(parts[2], 10));
                          }
                          return null;
                        }
                        // Go PascalCase union object: {content: {Type, Value: ...}}
                        // Extract the enum value and rebuild a proper MessageT.
                        if (
                          m !== null &&
                          typeof m === "object" &&
                          Object.keys(m as object).length > 0
                        ) {
                          const catKey = LN2_MESSAGE_CATEGORIES[g.category - 1]?.categoryKey;
                          const enumVal = extractEnumFromGoMember(m);
                          if (catKey && enumVal !== null) {
                            return buildMessageTForEnum(catKey, enumVal);
                          }
                          return null;
                        }
                        return null;
                      })
                      .filter((m): m is NonNullable<typeof m> => m !== null);
                    // Dedup members within an entry by extracted enum value.
                    // Legacy server data has been observed with two members
                    // resolving to the same enum (e.g. "Down, Parked, Down")
                    // which violates the one-hot-exclusion semantics. Healing
                    // on load means the user sees the correct unique set and
                    // a subsequent save persists it cleanly.
                    const seen = new Set<number>();
                    const dedupedMembers = (validMembers as MessageT[]).filter((m) => {
                      const ev = extractEnumValueFromMessageT(m);
                      if (ev < 0 || seen.has(ev)) return false;
                      seen.add(ev);
                      return true;
                    });
                    return new OneHotExclusionEntryT(dedupedMembers);
                  })
                  .filter((e) => e.members.length > 0);
                return new OneHotExclusionGroupT(
                  g.category,
                  categoryWide,
                  validEntries,
                  preservePositive,
                  evictOnlyWhenActive,
                  emitDeactivations,
                );
              },
            ),
          );
        }
        setCdoState(newCdo);
        setShowCdoEditor(true);

        // Store existing key IDs for edit mode so syncKeysForAdapter can
        // delete them before re-posting. resolvedKeys was populated above.
        if (formAction === "edit") {
          setEditingKeyIds(
            resolvedKeys.map((k: any) => formatUuidT(k.id)).filter((id: string) => id !== ""),
          );
          // Snapshot the post-hydration / pre-edit state for the smart
          // downstream-invalidation diff in the PUT save handler. We use
          // the local resolved values rather than the React state
          // (setAdapterName/setCdoState are async — the state hasn't
          // updated by the time this line runs). "duplicate" intentionally
          // skips the snapshot because the editor treats it as a fresh
          // create, never a diff against the source adapter.
          editSnapshotRef.current = buildAdapterSnapshot({
            name: adapterData.name || "",
            protocolType: resolvedProtocol.toUpperCase(),
            active: adapterData.active ?? true,
            cdoState: newCdo,
          });
        }
      } catch (err: unknown) {
        // Same RTK-Query error shape the save path sees — surface the
        // backend's "message" field (e.g. 404 → "No adapter with that id
        // exists") instead of the generic fallback.
        setSaveError(`Load error: ${formatAdapterApiError(err, "Failed to load adapter data")}`);
        // If the load failed because the wizard's server is gone, bubble
        // it up so the top-level banner explains what happened. The GET
        // itself is /adapters/:id (not server-scoped), but adapter
        // controllers often run server-scope authorization first and emit
        // the same envelope when the server is missing.
        const staleMsg = detectStaleServerErrorMessage(err);
        if (staleMsg && wizardCtx) {
          wizardCtx.reportStaleServerError(staleMsg);
        }
      }
    };
    loadAdapter();
  }, [existingAdapterId, formAction]);

  // Reset the unresolved-legacy-entries alert when the user switches adapters.
  useEffect(() => {
    setUnresolvedAlertDismissed(false);
  }, [existingAdapterId]);

  // An MCE create that the server refuses is one plain message with Get log.
  useEffect(() => {
    if (!isErrorPost || protocolType.toUpperCase() !== MCE_BINARY_CDO_CONFIG.protocolKey) return;
    showMceSaveFailure({
      message:
        "The adapter could not be created. Try again. If this continues, click Get log and send the file to technical support.",
      detail: `create: ${JSON.stringify(errorPost)}`,
    });
  }, [isErrorPost, errorPost]);

  // Handle successful POST
  useEffect(() => {
    if (!isSuccessPost || !dataPost) return;
    const adapterData = dataPost.data || dataPost;
    const rawId = adapterData.id;
    if (!rawId) return;
    const adapterId = formatUuidT(rawId) || String(rawId);

    const postCreateTasks = async () => {
      setSaveError(null);
      setSaveVerified(null);
      const token = await GETJwtToken();

      const storedMce = cdoState.cdoMce.stored;
      if (
        protocolType.toUpperCase() === MCE_BINARY_CDO_CONFIG.protocolKey &&
        storedMce?.binaryBase64
      ) {
        const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
        const refused = await saveUploadedMapping(
          `${apiBase}/adapters/${adapterId}/cdo`,
          token,
          storedMce.binaryBase64,
          null,
        );
        if (refused) {
          showMceSaveFailure({
            ...refused,
            message: `The adapter was created without its mapping. Open it and upload the files again. ${refused.message}`,
          });
          setSaveVerified(false);
          onSaveSuccess?.(adapterId, adapterName, protocolType, false, [], refused.message);
          return;
        }
      }

      // Verify CDO fields before binary CDO is sent, for the same reason as handleUpdate.
      const { valid, message: verifyMessage } = await verifyAdapterSave(adapterId, token);

      if (protocolType.toUpperCase() === "MODBUS") {
        await sendModbusCdoBinary(adapterId, token);
      }
      if (protocolType.toUpperCase() === "OPCUA") {
        await sendOpcUaCdoBinary(adapterId, token);
      }
      if (protocolType.toUpperCase() === "LOBBYVISION2") {
        await sendLobbyVision2CdoBinary(adapterId, token);
      }

      if (cdoState.ln1KeyMappings.length > 0) {
        await postKeysForAdapter(adapterId, cdoState.ln1KeyMappings, liftType, token);
      }
      if (valid) {
        setSaveVerified(true);
        onSaveSuccess?.(adapterId, adapterName, protocolType, true, [], verifyMessage ?? "Valid");
      } else {
        setSaveError(verifyMessage);
        setSaveVerified(false);
        onSaveSuccess?.(
          adapterId,
          adapterName,
          protocolType,
          false,
          [],
          verifyMessage ?? undefined,
        );
      }
      clearForm();
    };
    postCreateTasks();
  }, [isSuccessPost, dataPost]);

  // ************************************************ */
  // LABELS **************************************** */
  const getSaveButtonLabel = () => {
    if (formAction === "edit" && !isDirty) return "Verify";
    if (formAction === "edit") return "Update Adapter";
    if (formAction === "duplicate") return "Duplicate Adapter";
    return mode === "wizard" ? "+ Add Adapter" : "Create Adapter";
  };

  const getSaveButtonHandler = () => {
    if (formAction === "edit" && !isDirty) return handleVerify;
    if (formAction === "edit") return handleUpdate;
    return handleCreate;
  };

  const getTitle = () => {
    if (formAction === "edit") return "Edit Adapter";
    if (formAction === "duplicate") return "Duplicate Adapter";
    return "Create New Adapter";
  };

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingPost || isLoadingPut}>
      {/* Save error banner — shown when PUT/POST fails or read-back detects mismatch */}
      {saveError && (
        <Alert
          type={mceSaveFailure?.saved ? "warning" : "error"}
          showIcon
          closable
          onClose={() => setSaveError(null)}
          message={mceSaveFailure?.saved ? "Adapter saved" : "Adapter not saved correctly"}
          description={saveError}
          action={
            mceSaveFailure ? (
              <Button
                size="small"
                onClick={() =>
                  downloadMceIdlLog({
                    when: new Date(),
                    action: "save",
                    product: String(cdoState.cdoMce.product_type),
                    files: [],
                    error: new Error(mceSaveFailure.detail),
                  })
                }
              >
                Get log
              </Button>
            ) : undefined
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {/* Contested-byte warning — bytes two different cars both claim.
          A warning, not a block: the mapping is the operator's to judge, and
          refusing the save would leave no way to store work in progress. It is
          shown alongside a success banner on purpose, because the save DID
          succeed and the CDO is still wrong. */}
      {contestedByteWarning && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setContestedByteWarning(null)}
          message="Two cars claim the same wire byte"
          description={contestedByteWarning}
          style={{ marginBottom: 12 }}
          data-testid="contested-byte-warning"
        />
      )}
      {/* Save success banner — shown after successful read-back verification */}
      {saveVerified === true && !saveError && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setSaveVerified(null)}
          message="Saved and verified"
          description="The adapter data was confirmed on the server."
          style={{ marginBottom: 12 }}
        />
      )}
      <DrawerRowDivider icon={faGears} title={getTitle()} />
      <WizardReadOnlyId
        label="Adapter ID"
        value={formAction === "edit" ? existingAdapterId : undefined}
      />
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <div className="container">
          {/* Row 1: Name + Protocol */}
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <FormInputText
                inputValue={adapterName}
                inputLabelName={t("general.name")}
                inputOnChange={onChangeFormValue}
                inputVariableName="name"
                isRequired
                disabledInput={false}
                formularyType={FormularyType.WIZARD}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <div>
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="generalStyles__inputLabel">Protocol Type</span>
              </div>
              <Select
                className="generalStyles__width100"
                size="large"
                showSearch
                optionFilterProp="children"
                value={protocolType || undefined}
                placeholder="Select protocol"
                onChange={onChangeProtocol}
                style={{ marginTop: 4 }}
              >
                {PROTOCOL_OPTIONS.map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Row 2: Lift Type + Version */}
          <div className="row">
            <div className="col-12 col-md-4 mt-3">
              <div>
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="generalStyles__inputLabel">Lift Type</span>
              </div>
              <Select
                className="generalStyles__width100"
                size="large"
                value={liftType || undefined}
                placeholder="Select lift type"
                onChange={(val: string) => {
                  setLiftType(val);
                  setIsDirty(true);
                }}
                allowClear
                style={{ marginTop: 4 }}
              >
                {LIFT_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-4 mt-3">
              <FormInputText
                inputValue={version}
                inputLabelName="Version"
                inputOnChange={onChangeFormValue}
                inputVariableName="version"
                isRequired
                disabledInput={false}
                formularyType={FormularyType.WIZARD}
              />
              {versionTouched && !isValidSemver(version) && (
                <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                  Must be valid semver (e.g. 1.0.0)
                </span>
              )}
            </div>
            {ln1FloorIndexing && (
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Vendor Floor Indexing</span>
                </div>
                <Select
                  className="generalStyles__width100"
                  size="large"
                  value={vendorFloorIndexing}
                  onChange={(val: VendorFloorIndexing) => {
                    // The dropdown and Position Floor Base are the SAME quantity —
                    // one a two-choice shortcut, one the general integer — so the
                    // dropdown writes the base and reads its value back from it.
                    setPositionFloorBase(val === VendorFloorIndexing.ZERO_BASED ? 0 : 1);
                    setIsDirty(true);
                  }}
                  style={{ marginTop: 4 }}
                >
                  <Select.Option value={VendorFloorIndexing.ONE_BASED}>
                    One-based (1 = first floor)
                  </Select.Option>
                  <Select.Option value={VendorFloorIndexing.ZERO_BASED}>
                    Zero-based (0 = first floor)
                  </Select.Option>
                </Select>
              </div>
            )}
            {isLn1FamilyProtocol(protocolType) && (
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <Tooltip title="Let one wire address mean different things by direction. Off, every key is translated both ways, exactly as before. On, each key gains a Direction control so an address written TO the controller can carry a different LN2 message than the same address read FROM it — which some controllers, such as the Swift CEC, genuinely do.">
                    <span className="generalStyles__inputLabel">Direction-Aware Keys</span>
                  </Tooltip>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Switch
                    data-testid="adapter-direction-aware-keys"
                    data-doc-label="Direction-Aware Keys"
                    checked={directionAwareKeys}
                    onChange={(checked: boolean) => {
                      setDirectionAwareKeys(checked);
                      setIsDirty(true);
                    }}
                  />
                </div>
              </div>
            )}
            <div className="col-12 col-md-4 mt-3">
              <div>
                <Tooltip
                  title={
                    ln1FloorIndexing
                      ? "The VALUE this vendor reports for its lowest floor: 0 for zero-based, 1 for one-based. Vendor Floor Indexing is the two-choice spelling of this same value."
                      : "The VALUE this controller reports at its lowest floor. 0 leaves floor numbers as they are. Setting 1 shifts every floor this adapter reads and writes — confirm with a capture first."
                  }
                >
                  <span className="generalStyles__inputLabel">Position Floor Base</span>
                </Tooltip>
              </div>
              <InputNumber
                data-testid="adapter-position-floor-base"
                data-doc-label="Position Floor Base"
                className="generalStyles__width100"
                size="large"
                value={positionFloorBase}
                min={0}
                onChange={(val) => {
                  const next = typeof val === "number" ? val : 0;
                  setPositionFloorBase(next);
                  // The dropdown is derived from this base, so it follows. A
                  // base above 1 has no two-choice spelling and reads as
                  // one-based, which is what the server does with it.
                  setIsDirty(true);
                }}
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-4 mt-3">
              <div>
                <Tooltip
                  title={
                    ln1FloorIndexing
                      ? "The BIT INDEX this vendor uses for its lowest floor. Car and hall calls are bitmasks, so this is separate from the position base."
                      : "The bit or register slot this controller uses for its lowest floor. 0 leaves calls as they are. Changing it moves every call this adapter reads and writes."
                  }
                >
                  <span className="generalStyles__inputLabel">Call Floor Base</span>
                </Tooltip>
              </div>
              <InputNumber
                data-testid="adapter-call-floor-base"
                data-doc-label="Call Floor Base"
                className="generalStyles__width100"
                size="large"
                value={callFloorBase}
                min={0}
                onChange={(val) => {
                  setCallFloorBase(typeof val === "number" ? val : 0);
                  setIsDirty(true);
                }}
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-4 mt-3">
              <div>
                <Tooltip title="Hard ceiling on per-car expansion. Empty means no cap. While nothing has been entered, a cap is derived from the offsets automatically; anything you enter — including clearing the box — is left alone from then on.">
                  <span className="generalStyles__inputLabel">Max Cars</span>
                </Tooltip>
              </div>
              <InputNumber
                data-testid="adapter-max-cars"
                data-doc-label="Max Cars"
                className="generalStyles__width100"
                size="large"
                value={maxCars}
                placeholder="No cap"
                min={1}
                max={MAX_CARS_PROJECTION_CEILING}
                onChange={(val) => {
                  // NOT `: 0` — the floor-base habit two fields up would turn a
                  // cleared box into a cap of zero cars. Clearing re-arms the
                  // auto-detect for the next projection.
                  setMaxCars(typeof val === "number" ? val : null);
                  // "manual" even when the box is now EMPTY. Clearing it is an
                  // opinion — "no cap" — and treating it as "nothing expressed"
                  // is what let auto-detection undo it on the next projection.
                  setMaxCarsOrigin("manual");
                  setIsDirty(true);
                }}
                style={{ width: "100%", marginTop: 4 }}
              />
              {renderMaxCarsCaption(maxCars, maxCarsOrigin, maxCarsProjection, enumeratedCarCount)}
            </div>
          </div>
        </div>
      </div>
      <br />
      <DrawerRowDivider icon={faLock} title="Authentication" />
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        {/* AUTH SECTION */}
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-12 mt-3">
              <span className="generalStyles__inputLabel">Auth Type</span>
              <Select
                className="generalStyles__width100"
                size="large"
                value={authType || undefined}
                placeholder="Select auth type"
                onChange={(val: string) => {
                  setAuthType(val);
                  setIsDirty(true);
                }}
                style={{ marginTop: 4 }}
              >
                {AUTH_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {needsCredentials && (
              <>
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={username}
                    inputLabelName={authType === "APIKEY" ? "API Key Name" : "Username"}
                    inputOnChange={onChangeFormValue}
                    inputVariableName="username"
                    isRequired={!allowEmptyUsername}
                    disabledInput={false}
                    formularyType={FormularyType.WIZARD}
                  />
                  <Checkbox
                    checked={allowEmptyUsername}
                    onChange={(e) => {
                      setAllowEmptyUsername(e.target.checked);
                      setIsDirty(true);
                    }}
                    style={{ fontSize: 12, marginTop: 4 }}
                  >
                    Allow empty
                  </Checkbox>
                </div>
                <div className="col-12 col-md-6 mt-3">
                  <FormInputText
                    inputValue={password}
                    inputLabelName={authType === "APIKEY" ? "API Key Value" : "Password"}
                    inputOnChange={onChangeFormValue}
                    inputVariableName="password"
                    isRequired={!allowEmptyPassword && !passwordMasked}
                    disabledInput={false}
                    formularyType={FormularyType.WIZARD}
                    placeholder={passwordMasked ? t("adapter.passwordUnchanged") : undefined}
                  />
                  {passwordMasked ? (
                    // An empty password keeps the stored one for this caller,
                    // so "Allow empty" could never clear it: say what empty does.
                    <span
                      data-testid="adapter-password-unchanged-hint"
                      className="drawer__text"
                      style={{ fontSize: 12, marginTop: 4, display: "block" }}
                    >
                      {t("adapter.passwordUnchangedHint")}
                    </span>
                  ) : (
                    <Checkbox
                      checked={allowEmptyPassword}
                      onChange={(e) => {
                        setAllowEmptyPassword(e.target.checked);
                        setIsDirty(true);
                      }}
                      style={{ fontSize: 12, marginTop: 4 }}
                    >
                      Allow empty
                    </Checkbox>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <br />
      {/* OFFSET MATRIX — visible for LN1-family and MODBUS protocols */}
      {(isLn1FamilyProtocol(protocolType) || protocolType.toUpperCase() === "MODBUS") && (
        <>
          <DrawerRowDivider icon={faGears} title="Offset Matrix" />
          {/* OVERLAP ANALYSIS — the report the save gate's "offset matrix has
              overlapping registers" message has nowhere else to send anyone.
              Always enabled: an operator commissioning a site needs the
              affirmative answer as much as the negative one, and a disabled
              button proves nothing. The destination radio sits out here rather
              than inside the report, because a user who chose "New window"
              could otherwise never reach it again from the app. */}
          <div className="generalStyles__flexCenter" style={{ marginBottom: 8 }}>
            <div className="generalStyles__mrFix">
              <Button
                data-testid="overlap-analysis"
                type={hasMatrixOverlaps ? "primary" : "default"}
                danger={hasMatrixOverlaps}
                onClick={handleOpenOverlapAnalysis}
              >
                {overlapReport.totalOccurrences > 0
                  ? `Overlap Analysis (${overlapReport.totalOccurrences})`
                  : "Overlap Analysis"}
              </Button>
            </div>
            <div className="generalStyles__mrFix">
              <Radio.Group
                data-testid="overlap-analysis-open-in"
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={overlapOpenIn}
                onChange={(e) =>
                  dispatch(updateOverlapAnalysisOpenIn(e.target.value as OverlapAnalysisTarget))
                }
                options={[
                  { label: "This window", value: "panel" },
                  { label: "New window", value: "window" },
                ]}
              />
            </div>
          </div>
          <OverlapAnalysisModal
            open={overlapPanelOpen}
            onClose={() => setOverlapPanelOpen(false)}
            report={overlapReport}
            adapterName={adapterName || "Unnamed adapter"}
            protocolType={protocolType}
          />
          {unresolvedLegacyEntries.length > 0 &&
            !unresolvedAlertDismissed &&
            (() => {
              const groups = dedupeUnresolvedEntries(unresolvedLegacyEntries);
              const maxDisplay = 20;
              const displayed = groups.slice(0, maxDisplay);
              const overflow = groups.length - displayed.length;
              const groupLines = displayed.map((g) => {
                const sub = g.message_sub_type ? ` "${g.message_sub_type}"` : "";
                return `type ${g.message_type}${sub} (${g.unitSummary})`;
              });
              if (overflow > 0) {
                groupLines.push(`+${overflow} more`);
              }
              return (
                <Alert
                  closable
                  type="warning"
                  style={{ marginBottom: 8 }}
                  message="Some legacy offset entries could not be matched to a matrix column"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {groupLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  }
                  onClose={() => setUnresolvedAlertDismissed(true)}
                />
              );
            })()}
          <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
            <AdapterOffsetMatrix
              protocolType={protocolType}
              value={adapterOffsetMap}
              onChange={handleAdapterOffsetChange}
              columns={adapterOffsetColumns}
              adapterMappedTypes={adapterMappedTypes}
              ln1KeyMappings={cdoState.ln1KeyMappings}
              intraContentFlags={cdoState.ln1IntraContentFlags}
              numCars={analysisCarCount}
              floors={floors}
              regTypes={modbusRegTypes}
              onOverlapChange={setHasMatrixOverlaps}
            />
          </div>
          <br />
        </>
      )}
      {/* CONFIGURE MAPPINGS BUTTON */}
      <Divider />
      <div className={`drawer__box__${theme}`}>
        <div className="container">
          <div className="row">
            <div className="generalStyles__flexCenter">
              <div className="generalStyles__mrFix">
                <Tooltip
                  title={
                    !isBasicFieldsValid()
                      ? "Fill in all required basic fields above before configuring mappings"
                      : ""
                  }
                >
                  <span>
                    <Button
                      type={showCdoEditor ? "default" : "primary"}
                      onClick={() => setShowCdoEditor(!showCdoEditor)}
                      disabled={!isBasicFieldsValid()}
                      size="large"
                    >
                      {showCdoEditor ? "Hide Mappings" : "Configure Mappings"}
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <div className="generalStyles__mrFix">
                {onCancel && (
                  <Button onClick={onCancel} size="large">
                    Cancel
                  </Button>
                )}
              </div>
              <div className="generalStyles__mrFix">
                <Tooltip title={!isFullyValid ? getAddButtonTooltip() : ""} placement="topRight">
                  <span>
                    <Button
                      type="primary"
                      onClick={getSaveButtonHandler()}
                      disabled={!isFullyValid}
                      loading={isLoadingPost || isLoadingPut}
                      size="large"
                    >
                      {getSaveButtonLabel()}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CDO EDITOR SECTION */}
      {showCdoEditor && protocolType && (
        <StepAdaptersCdoEditor
          adapterId={formAction === "edit" ? existingAdapterId : undefined}
          directionAware={directionAwareKeys}
          protocolType={protocolType}
          cdo={cdoState}
          setCdo={handleCdoChange}
          units={units}
          floors={floors}
          onUnitsChange={handleUnitsChange}
          bankContext={bankContext}
          defaultDevType={
            (Object.entries(DevType).find(
              ([k, v]) => typeof v === "number" && k.toLowerCase() === liftType.toLowerCase(),
            )?.[1] as number) ?? DevType.Elevator
          }
        />
      )}
      {/* BOTTOM SAVE BUTTON — visible below the CDO editor so the user
          doesn't have to scroll up after making exclusion/mapping changes */}
      {showCdoEditor && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0" }}>
          <Tooltip title={!isFullyValid ? getAddButtonTooltip() : ""} placement="topRight">
            <span>
              <Button
                type="primary"
                onClick={getSaveButtonHandler()}
                disabled={!isFullyValid}
                loading={isLoadingPost || isLoadingPut}
                size="large"
              >
                {getSaveButtonLabel()}
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
      {/* ALERTS */}
      <GlobalAlert2
        isError={isErrorPost && protocolType.toUpperCase() !== MCE_BINARY_CDO_CONFIG.protocolKey}
        isSuccess={isSuccessPost}
        requestType="POST"
        error={errorPost}
        name="Adapter"
      />
    </Spin>
  );
}
